import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { supabase } from '../lib/supabaseClient';
import { fetchArbolData, crearNegocio, validarMision, calcularEstado } from '../lib/arbolData';
import { compressImage } from '../lib/compressImage';
import { useGenioLuca } from '../lib/useGenioLuca';
import { startCheckout } from '../lib/checkout';
import LocationCascade from '../components/locations/LocationCascade';

/* ── Design Tokens ─────────────────────────────── */
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const T = {
  bg: '#070A12', s1: '#0A0F1B', s2: '#111726', s3: '#1A2236',
  border: '#1E2740', text: '#F3F4F6', muted: '#8C97B0', dim: '#6B7084',
  teal: '#5dc1b9', mango: '#FDBE02', coral: '#eb636b',
};
const CLIP = 'polygon(10px 0,100% 0,100% calc(100% - 10px),calc(100% - 10px) 100%,0 100%,0 10px)';

const GIROS = ['Artesania y manufactura','Moda y accesorios','Alimentos y bebidas','Tecnologia','Servicios profesionales','Comercio','Turismo y experiencias','Otro'];
const STAGES = [
  { value: 'idea', label: 'Tengo una idea' },
  { value: 'arrancando', label: 'Arrancando (0-6 meses)' },
  { value: 'validando', label: 'Validando (6-18 meses)' },
  { value: 'estable', label: 'Estable (1-3 anos)' },
  { value: 'creciendo', label: 'Creciendo (3+ anos)' },
  { value: 'escalando', label: 'Escalando' },
];
const RAMAS = ['Cliente','Producto','Admin.','Legal','Equipo','Marca','Innov.','Fondeo'];
const RAMAS_FULL = ['Cliente','Producto','Administración','Legal','Equipo','Marca','Innovación','Fondeo'];
const ERA_NAMES = { 0:'Existencia', 1:'Supervivencia', 2:'Éxito', 3:'Despegue', 4:'Madurez' };
const ERA_INSIGNIAS = { 0:"It's Alive!", 1:'Still Standing', 2:'Boss Mode', 3:'Liftoff', 4:'Legacy' };
const ERA_FRASES = {
  0: 'Existe. Vende. Tiene nombre y cuenta. Ya no es una idea: es alguien.',
  1: 'Sobrevivió el año más difícil. La mayoría no llega aquí.',
  2: 'Ya es empresa y opera sin ti. Ahora eres el jefe, no el empleado.',
  3: 'Otros pusieron dinero en tu negocio. Eso cambia todo: ahora rindes cuentas.',
  4: 'Construiste algo que te trasciende. Eso es una empresa.',
};
const RAMA_INSIGNIAS = {
  'Clean Books': { rama: 'Administración', frase: 'Tres meses sabiendo cuánto ganó él y cuánto te pagó a ti. Eso es más de lo que hace la mayoría.' },
  'Asset Manager': { rama: 'Administración', frase: 'Ya sabes qué tiene tu negocio. Y lo que no está asegurado, se pierde.' },
  'Payroll Boss': { rama: 'Administración', frase: 'Tu negocio ya les paga a otros. Y a ti también, sin excepciones.' },
};
const CROWDFUNDING_INSIGNIAS = {
  'Fine Print': 'Leíste y firmaste. Pedir dinero es un compromiso, no un favor.',
  'Go Live': 'Tu negocio ya está pidiendo en público. Ahora cada peso que entre tiene nombre y destino.',
  'First Believer': 'Alguien confió en tu negocio antes que en ti. Cuídalo.',
  'Halfway There': 'La mitad. Y ni un peso de eso es para tu bolsillo, acuérdate.',
  'Fully Funded': 'Meta cumplida. El dinero entra a la cuenta de T&D y de ahí a la de tu negocio. Nunca a la tuya.',
  'Receipts': 'Mostraste en qué se fue cada peso. Eso es lo que te deja pedir otra vez.',
  'Trusted': 'Pediste, recibiste y rendiste cuentas. Tu negocio ya tiene reputación. Eso vale más que el dinero.',
  'Ghosted': 'No reportaste. Tus donantes no saben dónde quedó su dinero. Se pausa tu habilitación hasta que cumplas.',
};
/* Fallback Luca phrases per world (used if DB frase_luca is null) */
const MUNDO_FRASES = {
  0: 'Acabas de crear a alguien. Tiene nombre, tiene porqué. Desde hoy trabajas para él.',
  1: 'Primera venta. Ese dinero ya tiene dueño, y no eres tú: es de tu negocio.',
  2: 'Dos carteras. Él tiene su cuenta, tú tienes tu sueldo. Si tomas de más, se anota. Así se cuida a un socio.',
  3: 'Regresan por tu negocio, no por ti. Eso significa que ya existe sin que lo cargues.',
  4: 'Punto de equilibrio. Tu negocio ya se paga sus propios gastos. Ya no es tu bebé: es tu roomie.',
  5: 'Tiene acta, tiene RFC, tiene cuenta. Legalmente ya es otra persona. Felicidades, eres su jefe.',
  6: 'Funciona sin ti dos semanas. Tu negocio ya camina solo. Tú solo tienes que no estorbar.',
  7: 'Un extraño vería tus números y te prestaría. Eso es confianza, y la confianza se construye con cuentas claras.',
  8: 'Dinero ajeno entrando a la cuenta de tu negocio. Ahora rinde cuentas, porque ya no es solo tuyo.',
  9: 'Tu negocio ya te sobrevive. Tú puedes irte de vacaciones. Él no.',
};

/* Node positions for the tree (percentage-based, bottom-up zigzag) */
const NODE_POS = [
  { xPct: 50, y: 92 },  // 00 Raíz (center bottom)
  { xPct: 30, y: 82 },  // 01
  { xPct: 70, y: 72 },  // 02
  { xPct: 30, y: 62 },  // 03
  { xPct: 70, y: 49 },  // 04 (Supervivencia)
  { xPct: 30, y: 36 },  // 05
  { xPct: 70, y: 26 },  // 06
  { xPct: 50, y: 16 },  // 07
  { xPct: 30, y: 6 },   // 08
  { xPct: 70, y: 6 },   // 09
];

/* ── Injected CSS for animations ─────────────── */
const STYLE_TAG = `
@keyframes arbol-pulse{0%,100%{box-shadow:0 0 0 4px rgba(93,193,185,.18),0 0 14px rgba(93,193,185,.3)}50%{box-shadow:0 0 0 9px rgba(93,193,185,.06),0 0 28px rgba(93,193,185,.5)}}
@keyframes arbol-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@keyframes arbol-draw{from{stroke-dashoffset:var(--line-len,500)}to{stroke-dashoffset:0}}
.arbol-pulse{animation:arbol-pulse 1.8s ease-in-out infinite}
.arbol-float{animation:arbol-float 1.2s ease-in-out infinite}
`;

export default function ArbolPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, membership, refreshProfile } = useAuth();
  const { isMobile, isTablet } = useScreen();
  const useMobileLayout = isTablet; // <768px

  /* ── State ─────────────────────────────── */
  const [loading, setLoading] = useState(true);
  const [negocio, setNegocio] = useState(null);
  const [mundos, setMundos] = useState([]);
  const [misiones, setMisiones] = useState([]);
  const [progreso, setProgreso] = useState([]);
  const [step, setStep] = useState('loading'); // loading | welcome | form | tutorial | tree
  const [formName, setFormName] = useState('');
  const [formGiro, setFormGiro] = useState('');
  const [formGiroOtro, setFormGiroOtro] = useState('');
  const [formIdentidad, setFormIdentidad] = useState(''); // 'thinker' | 'doer'
  const [formStage, setFormStage] = useState('');
  const [formCountryId, setFormCountryId] = useState(null);
  const [formRegionId, setFormRegionId] = useState(null);
  const [formCity, setFormCity] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [selectedWorld, setSelectedWorld] = useState(0);
  const [modalMision, setModalMision] = useState(null);
  const [modalMode, setModalMode] = useState('hacer'); // hacer | yalo
  const [modalNote, setModalNote] = useState('');
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalEvidenciaUrl, setModalEvidenciaUrl] = useState(null);
  const [modalLinkValue, setModalLinkValue] = useState('');
  const [showPremiumBlock, setShowPremiumBlock] = useState(false);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [victoryWorld, setVictoryWorld] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [showStartHint, setShowStartHint] = useState(false);

  const rightPanelRef = useRef(null);
  const centerPanelRef = useRef(null);
  const leftPanelRef = useRef(null);

  /* ── Mobile tab state ──────────────────── */
  const [mobileTab, setMobileTab] = useState('arbol'); // 'arbol' | 'mision' | 'chat' | 'carta' | 'perfil'
  const [bottomSheetWorld, setBottomSheetWorld] = useState(null);

  /* ── Perfil / Bitácora / Comunidad ───── */
  const [bitacora, setBitacora] = useState([]);
  const [showBitacoraModal, setShowBitacoraModal] = useState(false);
  const [bitacoraTitle, setBitacoraTitle] = useState('');
  const [bitacoraText, setBitacoraText] = useState('');
  const [bitacoraTag, setBitacoraTag] = useState('experiencia');
  const [bitacoraSaving, setBitacoraSaving] = useState(false);
  const [showComunidad, setShowComunidad] = useState(false);
  const [comunidadList, setComunidadList] = useState([]);
  const [comunidadLoaded, setComunidadLoaded] = useState(false);
  const [comunidadBusqueda, setComunidadBusqueda] = useState('');
  const [comunidadGiroFiltro, setComunidadGiroFiltro] = useState('Todos');

  /* ── Chat (Genio y Luca) ─────────────── */
  const { chatMsgs, setChatMsgs, chatLoading, sendMessage } = useGenioLuca(profile);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);

  // Sync mobile tab from URL ?tab= param
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['arbol','mision','chat','carta','perfil'].includes(tabFromUrl)) {
      setMobileTab(tabFromUrl);
    } else if (!tabFromUrl) {
      setMobileTab('arbol');
    }
  }, [searchParams]);

  const isFree = !membership || membership === 'free_observer';

  /* ── Data loading ─────────────────────── */
  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const data = await fetchArbolData(user.id);
      setMundos(data.mundos);
      setMisiones(data.misiones);
      setProgreso(data.progreso);

      // Load bitácora entries
      if (data.negocio) {
        const { data: bitacoraData } = await supabase
          .from('arbol_bitacora')
          .select('*')
          .eq('negocio_id', data.negocio.id)
          .order('created_at', { ascending: false });
        setBitacora(bitacoraData || []);
      }

      if (!data.negocio && profile?.business_name) {
        // Auto-create negocio from existing profile data
        const n = await crearNegocio(user.id, profile.business_name, profile.business_type || '');
        setNegocio(n);
        setStep('tree');
      } else if (!data.negocio) {
        setStep('welcome');
      } else {
        setNegocio(data.negocio);
        setStep('tree');
      }
    } catch (e) {
      console.error('[Arbol] load error', e);
    } finally {
      setLoading(false);
    }
  }, [user, profile]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Derived game state ───────────────── */
  const estado = useMemo(
    () => calcularEstado(mundos, misiones, progreso),
    [mundos, misiones, progreso]
  );

  // Auto-select active world on first load
  useEffect(() => {
    if (step === 'tree' && mundos.length) setSelectedWorld(estado.mundoActivo);
  }, [step, mundos.length, estado.mundoActivo]);

  /* ── Tour data ───────────────────────── */
  const TOUR_STEPS = [
    {
      zone: 'right',
      title: 'Aquí están tus misiones',
      text: 'Cada misión es un paso real de tu negocio. Dale clic a una para llenarla: así ganas pines y avanzas.',
      tip: 'Empieza por la primera: "Nombre de tu negocio".',
    },
    {
      zone: 'center',
      title: 'Aquí está tu camino',
      text: 'Son 10 mundos. Cuando completas todas las misiones de un mundo, vences a su jefe y se abre el siguiente.',
      tip: 'En el Mundo 07 tu negocio queda listo para pedir fondeo (crowdfunding).',
    },
    {
      zone: 'left',
      title: 'Lo que le hace falta a tu negocio',
      text: 'Aquí ves la salud administrativa y las ramas de tu negocio. Se llenan solas conforme completas misiones.',
      tip: 'Si algo sale en rojo, ahí es donde tienes que poner atención.',
    },
  ];

  useEffect(() => {
    if (step === 'tree' && profile && !profile.tour_arbol_visto && negocio) {
      setShowTour(true);
    }
  }, [step, profile, negocio]);

  const finishTour = async () => {
    setShowTour(false);
    setShowStartHint(true);
    await supabase.from('profiles').update({ tour_arbol_visto: true }).eq('id', user.id);
    setTimeout(() => setShowStartHint(false), 10000);
  };

  /* ── Handlers ─────────────────────────── */
  const handleCreateNegocio = async () => {
    if (!formName.trim() || !formIdentidad || formSaving) return;
    if (formGiro === 'Otro' && !formGiroOtro.trim()) return;
    setFormSaving(true);
    try {
      const finalGiro = formGiro === 'Otro' ? formGiroOtro.trim() : formGiro;
      const n = await crearNegocio(user.id, formName.trim(), finalGiro);
      setNegocio(n);
      // Update profiles with match fields + mark onboarding done
      await supabase.from('profiles').update({
        business_name: formName.trim(),
        business_type: finalGiro || null,
        business_stage: formStage || null,
        identidad: formIdentidad,
        country_id: formCountryId || null,
        region_id: formRegionId || null,
        city: formCity || null,
        onboarding_done: true,
        updated_at: new Date().toISOString(),
      }).eq('id', user.id);
      if (refreshProfile) await refreshProfile();
      setStep('tutorial');
    } catch (e) {
      console.error('[Arbol] crear negocio error', e);
    } finally {
      setFormSaving(false);
    }
  };

  const handleValidarMision = async () => {
    if (!modalMision || modalSubmitting) return;
    // Premium check: free users can only "vencer jefes" for worlds 0 and 1
    // But they CAN validate individual missions in any world
    // The block is on vencer jefes (completing worlds) >= 2
    setModalSubmitting(true);
    try {
      const evUrl = modalEvidenciaUrl || modalLinkValue || null;
      const evNivel = modalEvidenciaUrl ? 'foto' : modalLinkValue ? 'link' : 'nota';
      const nuevo = await validarMision(
        negocio.id,
        modalMision.id,
        modalMode === 'hacer' ? 'hacer' : 'ya_lo_tengo',
        evUrl,
        modalNote || null,
        evNivel,
      );
      const newProgreso = [...progreso, nuevo];
      setProgreso(newProgreso);
      setModalMision(null);
      setModalNote('');
      setModalEvidenciaUrl(null);
      setModalLinkValue('');

      // Check if completing this mission vencido the world
      const misionMundoId = modalMision.mundo_id;
      const mainMissions = misiones.filter(m => m.mundo_id === misionMundoId && !m.side_quest);
      const newProgresoSet = new Set(newProgreso.map(p => p.mision_id));
      const allMainDone = mainMissions.length > 0 && mainMissions.every(m => newProgresoSet.has(m.id));
      const prevMundoOk = misionMundoId === 0 || (estado.mundoVencidoMap[misionMundoId - 1] === true);
      const wasVencido = estado.mundoVencidoMap[misionMundoId];
      if (allMainDone && prevMundoOk && !wasVencido) {
        const w = mundos.find(m => m.id === misionMundoId);
        setVictoryWorld(w || null);
        setShowVictoryModal(true);
      }
    } catch (e) {
      console.error('[Arbol] validar error', e);
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleMissionClick = (mision) => {
    const done = progreso.some(p => p.mision_id === mision.id);
    if (done) return;

    // Premium block: if free user tries to validate mission in world >= 2,
    // check if completing it would "vencer" the world
    // Actually per spec: free users CAN fill all missions, but can only vencer jefes for worlds 0 and 1
    // So we allow the click, but after validation we check mundo completion
    // For UX: show premium block when they try to validate in world >= 2 and it would complete the world
    // Simpler: show block when clicking a mission in a world >= 2 that is not yet active for free
    if (isFree && mision.mundo_id >= 2) {
      // Check: would this complete the world?
      const worldMissions = misiones.filter(m => m.mundo_id === mision.mundo_id && !m.side_quest);
      const doneMissions = worldMissions.filter(m => progreso.some(p => p.mision_id === m.id));
      const wouldComplete = doneMissions.length === worldMissions.length - 1; // this is the last one
      if (wouldComplete) {
        setShowPremiumBlock(true);
        return;
      }
    }

    setModalMision(mision);
    setModalMode('hacer');
    setModalNote('');
  };

  /* ── Sub-data for selected world ──────── */
  const selWorld = mundos.find(w => w.id === selectedWorld) || mundos[0];
  const selMissions = misiones.filter(m => m.mundo_id === selectedWorld);
  const selDone = selMissions.filter(m => progreso.some(p => p.mision_id === m.id));
  const totalMain = misiones.filter(m => !m.side_quest).length;
  const pctComplete = totalMain > 0 ? Math.round((progreso.length / totalMain) * 100) : 0;

  /* ── Era grouping ─────────────────────── */
  const eras = useMemo(() => {
    const map = {};
    mundos.forEach(w => {
      if (!map[w.era]) map[w.era] = { name: ERA_NAMES[w.era] || `Era ${w.era}`, worlds: [], done: 0 };
      map[w.era].worlds.push(w);
      if (estado.mundoVencidoMap[w.id]) map[w.era].done++;
    });
    return Object.entries(map).map(([era, d]) => ({ era: Number(era), ...d }));
  }, [mundos, estado.mundoVencidoMap]);

  /* ── Render ────────────────────────────── */
  if (loading || step === 'loading') {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font, color: T.muted }}>
        Cargando tu arbol...
      </div>
    );
  }

  /* == WELCOME SCREEN == */
  if (step === 'welcome') return <WelcomeScreen onNext={() => setStep('form')} />;
  if (step === 'form') return (
    <FormScreen
      formName={formName} setFormName={setFormName}
      formGiro={formGiro} setFormGiro={setFormGiro}
      formGiroOtro={formGiroOtro} setFormGiroOtro={setFormGiroOtro}
      formIdentidad={formIdentidad} setFormIdentidad={setFormIdentidad}
      formStage={formStage} setFormStage={setFormStage}
      formCountryId={formCountryId} formRegionId={formRegionId} formCity={formCity}
      onLocationChange={({ countryId, regionId, cityName }) => {
        setFormCountryId(countryId);
        setFormRegionId(regionId);
        setFormCity(cityName || null);
      }}
      saving={formSaving} onSubmit={handleCreateNegocio}
    />
  );
  if (step === 'tutorial') return <TutorialScreen onStart={() => setStep('tree')} />;

  /* == MAIN TREE VIEW == */
  const initials = (negocio?.nombre || 'N').slice(0, 2).toUpperCase();

  /* Active world data for Mision tab */
  const activeWorld = mundos.find(m => m.id === estado.mundoActivo);
  const activeMissions = misiones.filter(m => m.mundo_id === estado.mundoActivo);
  const activeMainMissions = activeMissions.filter(m => !m.side_quest);
  const activeDone = activeMainMissions.filter(m => progreso.some(p => p.mision_id === m.id)).length;
  const activeTotal = activeMainMissions.length;

  /* ── MOBILE FULL LAYOUT ──────────────── */
  if (useMobileLayout) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', fontFamily: font, color: T.text, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <style>{STYLE_TAG}{`
@keyframes mobile-sheet-up{from{transform:translateY(100%)}to{transform:translateY(0)}}
@keyframes mobile-fade-in{from{opacity:0}to{opacity:1}}
        `}</style>

        {/* === TAB: ARBOL === */}
        {mobileTab === 'arbol' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Compact header 44px */}
            <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', background: T.s1, borderBottom: `1px solid ${T.border}`, flexShrink: 0, gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{negocio?.nombre || 'Mi negocio'}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.dim, whiteSpace: 'nowrap' }}>
                LVL {String(estado.nivel).padStart(2,'0')} &middot; {estado.pines} pines
              </span>
            </div>

            {/* Full-screen tree (scrollable) */}
            <MobileTreeCanvas
              mundos={mundos} misiones={misiones} progreso={progreso} estado={estado}
              onNodeTap={(wId) => {
                if (bottomSheetWorld === wId) {
                  setBottomSheetWorld(null);
                } else {
                  setSelectedWorld(wId);
                  setBottomSheetWorld(wId);
                }
              }}
              bottomSheetWorld={bottomSheetWorld}
            />

            {/* Bottom Sheet */}
            {bottomSheetWorld !== null && (() => {
              const sheetWorld = mundos.find(w => w.id === bottomSheetWorld);
              if (!sheetWorld) return null;
              const sheetMissions = misiones.filter(m => m.mundo_id === bottomSheetWorld);
              const sheetMainMissions = sheetMissions.filter(m => !m.side_quest);
              const sheetDoneCount = sheetMainMissions.filter(m => progreso.some(p => p.mision_id === m.id)).length;
              const krs = sheetWorld.krs || [];
              return (
                <div style={{ position: 'fixed', inset: 0, zIndex: 150, animation: 'mobile-fade-in .2s ease' }}
                  onClick={() => setBottomSheetWorld(null)}>
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(7,10,18,.7)' }} />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '70vh',
                    background: T.s1, borderRadius: '20px 20px 0 0',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    animation: 'mobile-sheet-up .25s ease',
                  }} onClick={e => e.stopPropagation()}>
                    {/* Drag handle + close */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 16px 8px', flexShrink: 0, position: 'relative' }}>
                      <div style={{ width: 40, height: 4, background: T.border, borderRadius: 2 }} />
                      <button onClick={() => setBottomSheetWorld(null)} style={{
                        position: 'absolute', right: 16, width: 32, height: 32, background: T.s2,
                        border: `1px solid ${T.border}`, borderRadius: '50%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        color: T.muted, fontSize: 14, lineHeight: 1,
                      }}>&#10005;</button>
                    </div>
                    {/* Sheet content */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', color: T.dim, marginBottom: 4 }}>
                        Mundo {String(sheetWorld.id).padStart(2,'0')} &middot; Era {(sheetWorld.era || 0) + 1} &middot; {ERA_NAMES[sheetWorld.era] || ''}
                      </div>
                      <div style={{ fontFamily: serif, fontSize: 22, color: T.text, marginBottom: 4 }}>{sheetWorld.nombre}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.dim, textTransform: 'uppercase', marginBottom: 12 }}>
                        {sheetDoneCount} de {sheetMainMissions.length} misiones &middot; Insignia: {sheetWorld.insignia || '—'}
                      </div>

                      {/* OKR box */}
                      <div style={{ background: 'rgba(253,190,2,.05)', border: '1px solid rgba(253,190,2,.3)', borderLeft: `3px solid ${T.mango}`, borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', color: T.mango, marginBottom: 4 }}>Objetivo</div>
                        <div style={{ fontSize: 12, color: T.text, lineHeight: 1.4 }}>{sheetWorld.objetivo}</div>
                        {krs.length > 0 && (
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 6, paddingLeft: 8, borderLeft: `1px solid ${T.border}` }}>
                            {krs.map((kr, i) => <div key={i}>KR{i+1}: {typeof kr === 'string' ? kr : kr.texto || ''}</div>)}
                          </div>
                        )}
                      </div>

                      {/* Insignia hex */}
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', color: T.dim, marginBottom: 8 }}>Insignia</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <div style={{ width: 48, height: 56, clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', fontSize: 7, fontWeight: 700, textAlign: 'center', padding: 4, background: estado.mundoVencidoMap[sheetWorld.id] ? 'rgba(253,190,2,.2)' : T.s3, color: estado.mundoVencidoMap[sheetWorld.id] ? T.mango : T.dim }}>
                            <span>{sheetWorld.insignia || '?'}</span>
                          </div>
                          {estado.mundoVencidoMap[sheetWorld.id] && <span style={{ fontSize: 11, fontWeight: 700, color: T.teal, alignSelf: 'center' }}>&#10003; DESBLOQUEADA</span>}
                        </div>
                      </div>

                      {/* Mission list */}
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', color: T.dim, marginBottom: 8 }}>Misiones</div>
                      {sheetMissions.map(m => {
                        const done = progreso.some(p => p.mision_id === m.id);
                        return (
                          <div key={m.id} onClick={() => { if (!done) { handleMissionClick(m); } }}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: `1px solid ${T.border}`, cursor: done ? 'default' : 'pointer' }}>
                            <div style={{
                              width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                              background: done ? 'rgba(93,193,185,.2)' : 'transparent',
                              border: `1.5px solid ${done ? T.teal : T.border}`,
                              color: done ? T.teal : 'transparent',
                            }}>{done ? '✓' : ''}</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: done ? T.text : T.muted }}>{m.nombre}</div>
                              <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', background: done ? 'rgba(93,193,185,.15)' : T.s3, color: done ? T.teal : T.dim, border: done ? 'none' : `1px solid ${T.border}` }}>{m.rama}</span>
                                {m.evidencia && <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', background: 'transparent', border: `1px solid ${done ? T.mango : T.border}`, color: done ? T.mango : T.dim }}>{m.evidencia}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* === TAB: MISION === */}
        {mobileTab === 'mision' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '16px', background: T.s1, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.teal, letterSpacing: 1, marginBottom: 4, textTransform: 'uppercase' }}>
                MUNDO {String(estado.mundoActivo).padStart(2,'0')}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                {activeWorld?.nombre || 'Cargando...'}
              </div>
              {/* Progress bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 4, background: T.s3, borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${activeTotal > 0 ? (activeDone/activeTotal)*100 : 0}%`, background: T.teal, borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 11, color: T.dim, fontFamily: "'JetBrains Mono', monospace" }}>{activeDone}/{activeTotal}</span>
              </div>
            </div>

            {/* OKR summary box */}
            <div style={{ margin: '12px 16px', padding: '12px 14px', background: T.s2, borderLeft: `3px solid ${T.mango}`, clipPath: CLIP }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.mango, letterSpacing: 1, marginBottom: 4, textTransform: 'uppercase' }}>
                OBJETIVO &middot; lo que necesitas demostrar
              </div>
              <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
                {activeWorld?.objetivo || ''}
              </div>
            </div>

            {/* Scrollable mission list */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)', WebkitOverflowScrolling: 'touch' }}>
              {activeMissions.map(m => {
                const prog = progreso.find(p => p.mision_id === m.id);
                const done = !!prog;
                const fotoUrl = prog?.evidencia_url;
                return (
                  <div key={m.id} style={{
                    padding: '12px 16px', background: T.s2, border: `1px solid ${done ? 'rgba(93,193,185,.3)' : T.border}`,
                    marginBottom: 8, marginLeft: 16, marginRight: 16,
                    borderRadius: 12,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                        background: done ? 'rgba(93,193,185,.2)' : 'transparent',
                        border: `1.5px solid ${done ? T.teal : T.dim}`,
                        color: done ? T.teal : 'transparent',
                      }}>{done ? '✓' : ''}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: done ? T.text : T.muted }}>{m.nombre}</div>
                        <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>{m.descripcion || ''}</div>
                        {fotoUrl && (
                          <img src={fotoUrl} alt={m.nombre} style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 8, border: `1px solid ${T.border}` }} />
                        )}
                        {done && prog?.evidencia_nota && (
                          <div style={{ fontSize: 12, color: T.dim, marginBottom: 6, fontStyle: 'italic', lineHeight: 1.5 }}>
                            "{prog.evidencia_nota.substring(0, 120)}{prog.evidencia_nota.length > 120 ? '...' : ''}"
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', background: 'rgba(93,193,185,.15)', color: T.teal }}>{m.rama}</span>
                          {m.evidencia && <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', background: 'transparent', border: `1px solid ${T.mango}`, color: T.mango }}>{m.evidencia}</span>}
                        </div>
                      </div>
                    </div>
                    {!done && (
                      <button onClick={() => handleMissionClick(m)} style={{
                        width: '100%', marginTop: 12, height: 48,
                        background: T.teal, color: T.bg, fontWeight: 700, fontSize: 14,
                        border: 'none', cursor: 'pointer', fontFamily: font,
                        clipPath: CLIP, borderRadius: 0,
                      }}>
                        Ya lo tengo &rarr;
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === TAB: CHAT (Genio y Luca) === */}
        {mobileTab === 'chat' && (() => {
          const suggestions = [
            `¿Qué sigue en Mundo ${String(estado.mundoActivo).padStart(2,'0')}?`,
            'Ayúdame con mi propósito',
            'Revisa mi evidencia',
          ];
          const handleChatSend = (text) => {
            const t = (text || chatInput || '').trim();
            if (!t) return;
            sendMessage(t);
            setChatInput('');
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
          };
          return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Chat header 44px */}
              <div style={{
                height: 44, flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', padding: '0 16px',
                background: T.s1, borderBottom: `1px solid ${T.border}`,
              }}>
                <div style={{ width: 60 }} />
                <span style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Genio y Luca</span>
                <button onClick={() => { setChatMsgs([]); setChatInput(''); }}
                  style={{
                    background: 'none', border: 'none', color: T.teal,
                    fontSize: 12, cursor: 'pointer', fontFamily: font,
                    whiteSpace: 'nowrap', padding: '8px 0', minHeight: 48,
                    display: 'flex', alignItems: 'center',
                  }}>Nueva charla</button>
              </div>

              {/* Messages area */}
              <div style={{
                flex: 1, overflowY: 'auto', padding: '12px 16px',
                WebkitOverflowScrolling: 'touch',
                display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                {chatMsgs.length === 0 && !chatLoading && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: T.muted, fontSize: 14, textAlign: 'center', padding: 32 }}>
                    <span style={{ fontSize: 32 }}>⚡◆</span>
                    <span>Pregúntale algo a Genio y Luca sobre tu negocio</span>
                  </div>
                )}
                {chatMsgs.map((msg, i) => {
                  const isUser = msg.role === 'user';
                  const isGenio = (msg.autor || '').toLowerCase().includes('genio');
                  const isLuca = (msg.autor || '').toLowerCase().includes('luca');
                  if (isUser) {
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{
                          maxWidth: '78%', background: T.s2, borderRadius: 16,
                          padding: '10px 14px', fontSize: 14, lineHeight: 1.45, color: T.text,
                        }}>{msg.texto || msg.content}</div>
                      </div>
                    );
                  }
                  return (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700,
                        background: isGenio ? T.mango : isLuca ? '#185FA5' : T.mango,
                        color: isGenio ? T.bg : '#fff',
                      }}>{isGenio ? '⚡' : isLuca ? '◆' : '⚡'}</div>
                      <div style={{ maxWidth: '78%' }}>
                        <div style={{
                          fontSize: 11, fontWeight: 700, marginBottom: 3,
                          color: isGenio ? T.mango : isLuca ? T.teal : T.mango,
                        }}>{msg.autor || 'Genio & Luca'}</div>
                        <div style={{
                          background: isGenio ? 'rgba(253,190,2,.06)' : isLuca ? 'rgba(93,193,185,.06)' : 'rgba(253,190,2,.06)',
                          borderLeft: `2px solid ${isGenio ? T.mango : isLuca ? T.teal : T.mango}`,
                          borderRadius: '4px 16px 16px 4px', padding: '10px 14px',
                          fontSize: 14, lineHeight: 1.45, color: T.text,
                        }}>{msg.texto || msg.content}</div>
                      </div>
                    </div>
                  );
                })}
                {chatLoading && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700, background: T.mango, color: T.bg,
                    }}>⚡</div>
                    <div style={{
                      background: 'rgba(253,190,2,.06)', borderLeft: `2px solid ${T.mango}`,
                      borderRadius: '4px 16px 16px 4px', padding: '10px 14px',
                      fontSize: 14, color: T.muted, fontStyle: 'italic',
                    }}>Genio está pensando...</div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Suggestion chips */}
              <div style={{
                display: 'flex', gap: 8, padding: '8px 16px', overflowX: 'auto',
                flexShrink: 0, WebkitOverflowScrolling: 'touch',
              }}>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => handleChatSend(s)}
                    style={{
                      flexShrink: 0, border: `1px solid ${T.teal}`, background: 'transparent',
                      color: T.teal, fontSize: 12, padding: '8px 16px', borderRadius: 20,
                      cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap',
                      minHeight: 48, display: 'flex', alignItems: 'center',
                    }}>{s}</button>
                ))}
              </div>

              {/* Input bar */}
              <div style={{
                display: 'flex', gap: 8, padding: '8px 16px',
                paddingBottom: 'calc(8px + 56px + env(safe-area-inset-bottom, 0px))',
                flexShrink: 0, background: T.bg,
                borderTop: `1px solid ${T.border}`,
              }}>
                <input
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleChatSend(); }}
                  placeholder="Escribe a Genio y Luca..."
                  style={{
                    flex: 1, height: 44, borderRadius: 24, border: `1px solid ${T.border}`,
                    background: T.s2, color: T.text, fontSize: 14, padding: '0 16px',
                    fontFamily: font, outline: 'none',
                  }}
                />
                <button onClick={() => handleChatSend()}
                  style={{
                    width: 44, height: 44, borderRadius: '50%', border: 'none',
                    background: T.teal, color: '#fff', cursor: 'pointer', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700,
                  }}>&rarr;</button>
              </div>
            </div>
          );
        })()}

        {/* === TAB: CARTA (Business Card) === */}
        {mobileTab === 'carta' && (() => {
          const saludAdmin = calcularSaludAdmin(misiones, progreso);
          const saludColor = saludAdmin >= 70 ? T.teal : saludAdmin >= 40 ? T.mango : T.coral;
          const saludLabel = saludAdmin < 40 ? 'En riesgo' : saludAdmin < 70 ? 'Progresando' : 'Saludable';
          const stackSlotNames = ['Comunicación','Cobro','Facturación','Finanzas','Diseño','Redes','Clientes','Operación'];
          const unlockedStack = mundos
            .filter(w => estado.mundoVencidoMap[w.id] && w.stack_upgrade)
            .map(w => w.stack_upgrade);
          const allInsignias = mundos.map(w => ({
            name: w.insignia || `M${String(w.id).padStart(2,'0')}`,
            earned: !!estado.mundoVencidoMap[w.id],
          })).slice(0, 10);
          const cardInitials = (negocio?.nombre || 'NN').slice(0, 2).toUpperCase();
          const monoStyle = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 };

          return (
            <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
              {/* Header */}
              <div style={{
                height: 44, flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '0 16px',
                background: T.s1, borderBottom: `1px solid ${T.border}`,
              }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Mi carta de negocio</span>
              </div>

              {/* Card */}
              <div style={{
                margin: 16, border: `2px solid ${T.mango}`, borderRadius: 24,
                background: T.s1, padding: 24, overflow: 'auto',
              }}>
                {/* Avatar + Name */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                  <div style={{
                    width: 72, height: 72, borderRadius: '50%', background: T.s2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, fontWeight: 700, color: T.mango, marginBottom: 10,
                  }}>{cardInitials}</div>
                  <div style={{ fontFamily: serif, fontSize: 24, textAlign: 'center', marginBottom: 4 }}>{negocio?.nombre || 'Mi Negocio'}</div>
                  <div style={{ fontSize: 13, color: T.muted, textAlign: 'center' }}>
                    {negocio?.giro || 'Sin giro'} &middot; {profile?.city || 'México'}
                  </div>
                </div>

                {/* MUNDO ACTUAL */}
                <div style={{ marginBottom: 20 }}>
                  <div style={monoStyle}>Mundo actual</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.teal, flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>
                      {String(estado.mundoActivo).padStart(2,'0')} &middot; {mundos.find(m => m.id === estado.mundoActivo)?.nombre || '—'}
                    </span>
                    <span style={{
                      marginLeft: 'auto', fontSize: 11, fontWeight: 700,
                      background: 'rgba(93,193,185,.15)', color: T.teal,
                      padding: '2px 8px', borderRadius: 10,
                    }}>{activeDone}/{activeTotal}</span>
                  </div>
                </div>

                {/* PRODUCTO ESTRELLA */}
                <div style={{ marginBottom: 20 }}>
                  <div style={monoStyle}>Producto estrella</div>
                  {negocio?.producto_estrella ? (
                    <div style={{ fontSize: 14, color: T.text }}>&#9733; {negocio.producto_estrella}</div>
                  ) : (
                    <div style={{ fontSize: 14, color: T.dim, fontStyle: 'italic' }}>Completa la misión en Mundo 01</div>
                  )}
                </div>

                {/* SALUD ADMINISTRATIVA */}
                <div style={{ marginBottom: 20 }}>
                  <div style={monoStyle}>Salud administrativa</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: saludColor, marginBottom: 6 }}>{saludAdmin}</div>
                  <div style={{ height: 6, background: T.s3, borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ height: '100%', width: `${saludAdmin}%`, background: saludColor, borderRadius: 3, transition: '.4s' }} />
                  </div>
                  <div style={{ fontSize: 11, color: T.dim }}>{saludLabel}</div>
                </div>

                {/* INSIGNIAS */}
                <div style={{ marginBottom: 20 }}>
                  <div style={monoStyle}>Insignias</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {allInsignias.map((ins, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{
                          width: 36, height: 42,
                          clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 8, fontWeight: 700,
                          background: ins.earned ? 'rgba(253,190,2,.2)' : T.s3,
                          color: ins.earned ? T.mango : T.dim,
                          border: ins.earned ? `2px solid ${T.mango}` : 'none',
                        }}>
                          {(ins.name || '?').slice(0, 3).toUpperCase()}
                        </div>
                        <div style={{
                          fontSize: 8, marginTop: 2, textAlign: 'center', maxWidth: 36,
                          lineHeight: 1.1, color: ins.earned ? T.mango : T.dim,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{ins.name}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* STACK */}
                <div style={{ marginBottom: 20 }}>
                  <div style={monoStyle}>Stack</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {stackSlotNames.map((slot, i) => {
                      const filled = unlockedStack.find(s => s.toLowerCase().includes(slot.toLowerCase().slice(0, 4)));
                      return (
                        <span key={i} style={{
                          fontSize: 11, padding: '4px 10px', borderRadius: 8,
                          fontFamily: font, fontWeight: 500,
                          background: filled ? 'rgba(93,193,185,.1)' : T.s3,
                          color: filled ? T.teal : T.dim,
                          border: filled ? `1px solid rgba(93,193,185,.3)` : `1px solid ${T.border}`,
                        }}>{filled || slot}</span>
                      );
                    })}
                  </div>
                </div>

                {/* REDES */}
                <div style={{ marginBottom: 20 }}>
                  <div style={monoStyle}>Redes</div>
                  {negocio?.redes && Object.keys(negocio.redes).length > 0 ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {Object.entries(negocio.redes).map(([key, val]) => (
                        <div key={key} style={{
                          width: 36, height: 36, borderRadius: '50%', background: T.s3,
                          border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 10, fontWeight: 700, color: T.teal,
                          cursor: val ? 'pointer' : 'default',
                        }} onClick={() => val && window.open(val, '_blank')}>
                          {key.slice(0, 2).toUpperCase()}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 14, color: T.dim, fontStyle: 'italic' }}>Completa la misión en Mundo 01</div>
                  )}
                </div>

                {/* Footer */}
                <div style={{ textAlign: 'center', paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                  <button onClick={async () => {
                    if (!comunidadLoaded) {
                      try {
                        let { data: cData } = await supabase
                          .from('arbol_cartas_publicas')
                          .select('*')
                          .order('created_at', { ascending: false })
                          .limit(50);
                        if (!cData) throw new Error('view missing');
                        setComunidadList(cData);
                      } catch {
                        const { data: cData } = await supabase
                          .from('arbol_negocios')
                          .select('id, nombre, giro, salud_admin, slug, city, user_id')
                          .order('created_at', { ascending: false })
                          .limit(50);
                        setComunidadList(cData || []);
                      }
                      setComunidadLoaded(true);
                    }
                    setShowComunidad(true);
                  }}
                    style={{
                      background: 'none', border: 'none', color: T.teal,
                      fontSize: 14, cursor: 'pointer', fontFamily: font,
                      minHeight: 48, display: 'inline-flex', alignItems: 'center',
                    }}>Ver comunidad &rarr;</button>
                </div>
              </div>

              {/* Comunidad overlay */}
              {showComunidad && (() => {
                const comGiros = ['Todos', ...Array.from(new Set(comunidadList.map(n => n.giro).filter(Boolean)))];
                const comFiltrados = comunidadList.filter(n => {
                  const matchBusq = !comunidadBusqueda || (n.nombre || '').toLowerCase().includes(comunidadBusqueda.toLowerCase()) || (n.giro || '').toLowerCase().includes(comunidadBusqueda.toLowerCase());
                  const matchGiro = comunidadGiroFiltro === 'Todos' || n.giro === comunidadGiroFiltro;
                  return matchBusq && matchGiro;
                });
                return (
                <div style={{ position: 'fixed', inset: 0, zIndex: 250, background: T.bg, display: 'flex', flexDirection: 'column', fontFamily: font, color: T.text }}>
                  {/* Header */}
                  <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', padding: '0 16px', background: T.s1, borderBottom: `1px solid ${T.border}`, gap: 8 }}>
                    <button onClick={() => { setShowComunidad(false); setComunidadBusqueda(''); setComunidadGiroFiltro('Todos'); }} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 14, cursor: 'pointer', fontFamily: font, minHeight: 48, display: 'flex', alignItems: 'center' }}>&larr;</button>
                    <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Comunidad</span>
                  </div>

                  {/* Search */}
                  <div style={{ padding: '12px 16px 0' }}>
                    <input
                      type="text" value={comunidadBusqueda} onChange={e => setComunidadBusqueda(e.target.value)}
                      placeholder="Buscar proyecto..."
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 12,
                        background: T.s2, border: `1px solid ${T.border}`, color: T.text,
                        fontSize: 14, fontFamily: font, outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Giro chips */}
                  <div style={{ display: 'flex', gap: 8, padding: '12px 16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    {comGiros.map(g => (
                      <button key={g} onClick={() => setComunidadGiroFiltro(g)} style={{
                        padding: '4px 14px', borderRadius: 16, fontSize: 12, fontWeight: 500,
                        border: `1px solid ${comunidadGiroFiltro === g ? T.teal : T.border}`,
                        background: comunidadGiroFiltro === g ? 'rgba(93,193,185,.12)' : 'transparent',
                        color: comunidadGiroFiltro === g ? T.teal : T.muted,
                        cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap', flexShrink: 0,
                      }}>{g}</button>
                    ))}
                  </div>

                  {/* List */}
                  <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
                    {comFiltrados.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: T.dim }}>
                        <div style={{ fontSize: 14, marginBottom: 8 }}>{comunidadList.length === 0 ? 'Comunidad disponible proximamente' : 'No se encontraron proyectos'}</div>
                      </div>
                    ) : (
                      comFiltrados.map(neg => (
                        <div key={neg.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                          <div style={{ width: 48, height: 48, borderRadius: '50%', background: T.s2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: T.mango, flexShrink: 0 }}>
                            {(neg.nombre || 'N')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{neg.nombre}</div>
                            <div style={{ fontSize: 12, color: T.muted }}>{neg.giro || 'Sin giro'}{neg.city ? ` · ${neg.city}` : ''}</div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: (neg.salud_admin || 0) >= 70 ? T.teal : (neg.salud_admin || 0) >= 40 ? T.mango : T.coral, letterSpacing: 1 }}>Salud admin.</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: (neg.salud_admin || 0) >= 70 ? T.teal : (neg.salud_admin || 0) >= 40 ? T.mango : T.coral }}>{neg.salud_admin || 0}</div>
                          </div>
                        </div>
                      ))
                    )}
                    <div style={{ padding: 16, textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: T.dim, fontStyle: 'italic' }}>La red neuronal se ve mejor en pantalla grande</div>
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>
          );
        })()}

        {/* === TAB: PERFIL === */}
        {mobileTab === 'perfil' && (() => {
          const STACK_SLOTS = ['Comunicación','Cobro','Facturación','Finanzas','Diseño','Redes','Clientes (CRM)','Operación'];
          const perfilMonoLabel = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 10, marginTop: 20, padding: '0 16px' };

          const handleBitacoraSave = async () => {
            if (!bitacoraTitle.trim() || bitacoraSaving) return;
            setBitacoraSaving(true);
            try {
              const { data: newEntry, error } = await supabase
                .from('arbol_bitacora')
                .insert({ negocio_id: negocio.id, titulo: bitacoraTitle.trim(), texto: bitacoraText.trim() || null, tag: bitacoraTag })
                .select()
                .single();
              if (!error && newEntry) {
                setBitacora(prev => [newEntry, ...prev]);
              }
              setBitacoraTitle('');
              setBitacoraText('');
              setBitacoraTag('experiencia');
              setShowBitacoraModal(false);
            } catch (e) {
              console.error('[Bitacora] save error', e);
            } finally {
              setBitacoraSaving(false);
            }
          };

          return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Header 44px */}
              <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.s1, borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Mi perfil</span>
              </div>

              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 80px)' }}>

                {/* Profile header */}
                <div style={{ padding: 24, textAlign: 'center' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: T.s2, border: `2px solid ${T.teal}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 24, fontWeight: 700, color: T.teal }}>
                    {(negocio?.nombre || 'N')[0].toUpperCase()}
                  </div>
                  <div style={{ fontFamily: serif, fontSize: 18 }}>{negocio?.nombre || 'Mi negocio'}</div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{negocio?.giro || 'Sin giro'} &middot; {(profile?.membership === 'spark_seeker' || profile?.membership === 'doer') ? 'Doer' : 'Free Observer'}</div>
                  <button onClick={() => { window.location.href = '/perfil'; }} style={{ marginTop: 12, padding: '8px 20px', background: 'transparent', border: `1px solid ${T.border}`, color: T.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: font, borderRadius: 8 }}>Editar perfil</button>
                </div>

                {/* RAMAS */}
                <div style={perfilMonoLabel}>RAMAS</div>
                {RAMAS_FULL.map((rama, i) => {
                  const val = estado.stats[rama] || 0;
                  const max = 5;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '0 16px' }}>
                      <div style={{ width: 100, fontSize: 12, color: T.dim, textAlign: 'right', flexShrink: 0 }}>{rama}</div>
                      <div style={{ flex: 1, height: 4, background: T.s3, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(val / max) * 100}%`, background: T.teal, borderRadius: 2 }} />
                      </div>
                      <div style={{ width: 16, fontSize: 12, fontWeight: 700, color: val > 0 ? T.teal : T.dim, textAlign: 'right' }}>{val}</div>
                    </div>
                  );
                })}

                {/* STACK */}
                <div style={perfilMonoLabel}>STACK</div>
                <div style={{ padding: '0 16px' }}>
                  {STACK_SLOTS.map((slot, i) => {
                    const stackVal = negocio?.stack && typeof negocio.stack === 'object'
                      ? Object.values(negocio.stack).find(v => typeof v === 'string' && v.toLowerCase().includes(slot.toLowerCase().slice(0, 4)))
                      : null;
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.dim }}>{slot}</span>
                        <span style={{ fontSize: 14, color: stackVal ? T.text : T.dim }}>{stackVal || '\u2014'}</span>
                      </div>
                    );
                  })}
                </div>

                {/* ERAS */}
                <div style={perfilMonoLabel}>ERAS</div>
                <div style={{ padding: '0 16px' }}>
                  {[0, 1, 2, 3, 4].map(eraIdx => {
                    const eraData = eras.find(e => e.era === eraIdx);
                    const filled = eraData ? eraData.done : 0;
                    const total = eraData ? eraData.worlds.length : (eraIdx === 0 ? 4 : eraIdx === 1 ? 1 : eraIdx === 2 ? 2 : eraIdx === 3 ? 2 : 1);
                    return (
                      <div key={eraIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: T.dim, width: 100, flexShrink: 0 }}>
                          Era {eraIdx + 1} &middot; {ERA_NAMES[eraIdx]}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: filled > 0 ? T.teal : T.dim, width: 28, textAlign: 'center' }}>{filled}/{total}</span>
                        <span style={{ fontSize: 12, color: filled === total && total > 0 ? T.mango : T.dim, fontStyle: 'italic', flex: 1 }}>{ERA_INSIGNIAS[eraIdx]}</span>
                      </div>
                    );
                  })}
                </div>

                {/* BITACORA */}
                <div style={perfilMonoLabel}>BITACORA</div>
                <div style={{ padding: '0 16px' }}>
                  {bitacora.length === 0 ? (
                    <div style={{ fontSize: 14, color: T.dim, fontStyle: 'italic', padding: '12px 0' }}>Tus aprendizajes apareceran aqui</div>
                  ) : (
                    bitacora.map((entry, i) => (
                      <div key={entry.id || i} style={{ padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{entry.titulo}</span>
                          {entry.tag && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                              background: entry.tag === 'acierto' ? 'rgba(93,193,185,.15)' : entry.tag === 'error' ? 'rgba(235,99,107,.15)' : 'rgba(253,190,2,.15)',
                              color: entry.tag === 'acierto' ? T.teal : entry.tag === 'error' ? T.coral : T.mango,
                            }}>#{entry.tag}</span>
                          )}
                        </div>
                        {entry.texto && <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.4 }}>{entry.texto}</div>}
                      </div>
                    ))
                  )}
                </div>

                {/* Cerrar sesion */}
                <div style={{ padding: '24px 16px' }}>
                  <button onClick={async () => { await supabase.auth.signOut(); window.location.href = '/'; }} style={{
                    width: '100%', height: 48, background: 'transparent', border: `1px solid ${T.border}`,
                    color: T.coral, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: font, borderRadius: 8,
                  }}>Cerrar sesion</button>
                </div>
              </div>

              {/* Floating + button for bitacora */}
              <button onClick={() => setShowBitacoraModal(true)} style={{
                position: 'fixed', bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)', right: 16,
                width: 56, height: 56, borderRadius: '50%', background: T.teal, border: 'none',
                color: '#fff', fontSize: 24, fontWeight: 700, cursor: 'pointer', zIndex: 150,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(93,193,185,.4)',
              }}>+</button>

              {/* Bitacora modal */}
              {showBitacoraModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: T.bg, display: 'flex', flexDirection: 'column', fontFamily: font, color: T.text }}>
                  {/* Modal header */}
                  <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', padding: '0 16px', background: T.s1, borderBottom: `1px solid ${T.border}`, gap: 12 }}>
                    <button onClick={() => setShowBitacoraModal(false)} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 14, cursor: 'pointer', fontFamily: font, minHeight: 48, display: 'flex', alignItems: 'center' }}>&larr; Cancelar</button>
                    <span style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 600 }}>Nueva nota</span>
                    <div style={{ width: 70 }} />
                  </div>

                  <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: T.dim, marginBottom: 6, fontWeight: 600 }}>Titulo</div>
                      <input value={bitacoraTitle} onChange={e => setBitacoraTitle(e.target.value)} placeholder="Que aprendiste hoy?" style={{
                        width: '100%', background: T.s2, border: `1px solid ${T.border}`, color: T.text,
                        fontFamily: font, fontSize: 14, padding: '12px 14px', outline: 'none', borderRadius: 8, boxSizing: 'border-box',
                      }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: T.dim, marginBottom: 6, fontWeight: 600 }}>Detalle (opcional)</div>
                      <textarea value={bitacoraText} onChange={e => setBitacoraText(e.target.value)} placeholder="Escribe los detalles..." rows={4} style={{
                        width: '100%', background: T.s2, border: `1px solid ${T.border}`, color: T.text,
                        fontFamily: font, fontSize: 14, padding: '12px 14px', outline: 'none', borderRadius: 8, resize: 'vertical', boxSizing: 'border-box',
                      }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: T.dim, marginBottom: 8, fontWeight: 600 }}>Etiqueta</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {['acierto', 'error', 'experiencia'].map(tag => (
                          <button key={tag} onClick={() => setBitacoraTag(tag)} style={{
                            flex: 1, minHeight: 48, borderRadius: 8, cursor: 'pointer', fontFamily: font, fontSize: 14, fontWeight: 600,
                            background: bitacoraTag === tag ? (tag === 'acierto' ? 'rgba(93,193,185,.15)' : tag === 'error' ? 'rgba(235,99,107,.15)' : 'rgba(253,190,2,.15)') : T.s2,
                            color: bitacoraTag === tag ? (tag === 'acierto' ? T.teal : tag === 'error' ? T.coral : T.mango) : T.dim,
                            border: `1px solid ${bitacoraTag === tag ? (tag === 'acierto' ? T.teal : tag === 'error' ? T.coral : T.mango) : T.border}`,
                          }}>#{tag}</button>
                        ))}
                      </div>
                    </div>

                    <button onClick={handleBitacoraSave} disabled={!bitacoraTitle.trim() || bitacoraSaving} style={{
                      width: '100%', height: 48, background: bitacoraTitle.trim() && !bitacoraSaving ? T.teal : T.s3,
                      color: bitacoraTitle.trim() && !bitacoraSaving ? T.bg : T.dim,
                      fontSize: 14, fontWeight: 700, border: 'none', cursor: bitacoraTitle.trim() && !bitacoraSaving ? 'pointer' : 'default',
                      fontFamily: font, borderRadius: 8, marginTop: 8,
                    }}>{bitacoraSaving ? 'Guardando...' : 'Guardar'}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* MISSION MODAL (full screen on mobile) */}
        {modalMision && (
          <MissionModal
            mision={modalMision} mode={modalMode} setMode={setModalMode}
            note={modalNote} setNote={setModalNote}
            submitting={modalSubmitting} onSubmit={handleValidarMision}
            onClose={() => { setModalMision(null); setModalEvidenciaUrl(null); setModalLinkValue(''); }}
            mundos={mundos} misiones={misiones} selectedWorld={selectedWorld}
            negocioId={negocio?.id}
            evidenciaUrl={modalEvidenciaUrl} setEvidenciaUrl={setModalEvidenciaUrl}
            linkValue={modalLinkValue} setLinkValue={setModalLinkValue}
            useMobileLayout={true}
          />
        )}

        {/* VICTORY MODAL */}
        {showVictoryModal && (
          <VictoryModal world={victoryWorld} onClose={() => setShowVictoryModal(false)} />
        )}

        {/* PREMIUM BLOCK MODAL */}
        {showPremiumBlock && (
          <PremiumBlockModal onClose={() => setShowPremiumBlock(false)} navigate={navigate} user={user} profile={profile} />
        )}
      </div>
    );
  }

  /* ── DESKTOP LAYOUT ─────────────────── */
  return (
    <div style={{ background: T.bg, minHeight: '100vh', fontFamily: font, color: T.text }}>
      <style>{STYLE_TAG}</style>

      {/* HUD */}
      <div style={{ background: T.s1, borderBottom: `1px solid ${T.border}`, padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${T.teal}, ${T.s3})`, border: `2px solid ${T.teal}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: T.bg, flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>{negocio?.nombre || 'Mi Negocio'}</div>
        <HudStat icon="🌲" label="Nivel" value={String(estado.nivel).padStart(2, '0')} />
        <HudStat icon="⬡" label="" value={`${estado.pines} pines`} />
        <HudStat icon="🏅" label="" value={`${estado.insignias.length} insignias`} />
        <button onClick={() => { setTourStep(0); setShowTour(true); }} style={{
          width: 28, height: 28, borderRadius: '50%', background: 'rgba(93,193,185,.15)',
          border: '1px solid rgba(93,193,185,.3)', color: '#5dc1b9', fontSize: 13,
          fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontFamily: "'DM Sans', sans-serif",
        }}>?</button>
        <button onClick={() => navigate('/sala')} style={{ background: 'transparent', border: `1px solid ${T.border}`, color: T.muted, fontFamily: font, fontSize: 11, fontWeight: 700, padding: '5px 12px', cursor: 'pointer', clipPath: CLIP, letterSpacing: '.5px' }}>← LA SALA</button>
        <button onClick={() => navigate('/perfil')} style={{ background: 'transparent', border: `1px solid ${T.teal}`, color: T.teal, fontFamily: font, fontSize: 11, fontWeight: 700, padding: '5px 12px', cursor: 'pointer', clipPath: CLIP, letterSpacing: '.5px' }}>MI CARTA</button>
      </div>

      {/* ── DESKTOP 3-COL LAYOUT ──────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 420px', height: 'calc(100vh - 52px)', overflow: 'hidden' }}>
        {/* LEFT PANEL */}
        <div ref={leftPanelRef} style={{ overflow: 'hidden' }}>
          <LeftPanel
            negocio={negocio} estado={estado} eras={eras} misiones={misiones}
            progreso={progreso} pctComplete={pctComplete} mundos={mundos}
          />
        </div>
        {/* CENTER: Tree SVG */}
        <div ref={centerPanelRef} style={{ background: T.bg, position: 'relative', overflowY: 'auto', overflowX: 'hidden' }}>
          <TreeSVG
            mundos={mundos} estado={estado} selectedWorld={selectedWorld}
            setSelectedWorld={setSelectedWorld} misiones={misiones} progreso={progreso}
          />
        </div>
        {/* RIGHT PANEL */}
        <div ref={rightPanelRef} style={{ overflow: 'hidden' }}>
          <RightPanel
            selWorld={selWorld} selMissions={selMissions} selDone={selDone}
            progreso={progreso} misiones={misiones} onMissionClick={handleMissionClick}
            estado={estado} selectedWorld={selectedWorld}
          />
        </div>
      </div>

      {/* TOUR OVERLAY */}
      {showTour && (
        <TourOverlay
          step={tourStep}
          totalSteps={TOUR_STEPS.length}
          data={TOUR_STEPS[tourStep]}
          zoneRef={tourStep === 0 ? rightPanelRef : tourStep === 1 ? centerPanelRef : leftPanelRef}
          onNext={() => setTourStep(s => s + 1)}
          onPrev={() => setTourStep(s => s - 1)}
          onSkip={finishTour}
          onFinish={finishTour}
          useMobileLayout={useMobileLayout}
        />
      )}

      {/* START HINT */}
      {showStartHint && !modalMision && (
        <div style={{
          position: 'fixed', right: 440, bottom: '50%',
          background: '#FDBE02', color: '#070A12', padding: '8px 16px',
          borderRadius: 8, fontSize: 13, fontWeight: 700, zIndex: 150,
          animation: 'arbol-float 1.2s ease-in-out infinite',
          boxShadow: '0 4px 20px rgba(253,190,2,.3)',
        }}>
          Empieza aquí: dale clic a tu primera misión →
        </div>
      )}

      {/* MISSION MODAL */}
      {modalMision && (
        <MissionModal
          mision={modalMision} mode={modalMode} setMode={setModalMode}
          note={modalNote} setNote={setModalNote}
          submitting={modalSubmitting} onSubmit={handleValidarMision}
          onClose={() => { setModalMision(null); setModalEvidenciaUrl(null); setModalLinkValue(''); }}
          mundos={mundos} misiones={misiones} selectedWorld={selectedWorld}
          negocioId={negocio?.id}
          evidenciaUrl={modalEvidenciaUrl} setEvidenciaUrl={setModalEvidenciaUrl}
          linkValue={modalLinkValue} setLinkValue={setModalLinkValue}
        />
      )}

      {/* VICTORY MODAL */}
      {showVictoryModal && (
        <VictoryModal world={victoryWorld} onClose={() => setShowVictoryModal(false)} />
      )}

      {/* PREMIUM BLOCK MODAL */}
      {showPremiumBlock && (
        <PremiumBlockModal onClose={() => setShowPremiumBlock(false)} navigate={navigate} user={user} profile={profile} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════ */

function HudStat({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: T.muted }}>
      {icon} {label} <b style={{ color: T.mango, fontSize: 13 }}>{value}</b>
    </div>
  );
}

/* ── Welcome Screen ───────────────────────────── */
function WelcomeScreen({ onNext }) {
  return (
    <div style={{ background: T.bg, backgroundImage: `radial-gradient(ellipse 80% 60% at 50% 100%, rgba(93,193,185,.08) 0%, transparent 70%)`, minHeight: '100vh', fontFamily: font, color: T.text }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(93,193,185,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(93,193,185,.03) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '80px 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: serif, fontSize: 13, letterSpacing: 4, color: T.teal, marginBottom: 48, textTransform: 'uppercase' }}>Thinker & Doer</div>
        <h1 style={{ fontFamily: serif, fontSize: 'clamp(32px,6vw,56px)', lineHeight: 1.1, marginBottom: 16, margin: '0 0 16px' }}>
          Haz crecer tu negocio.<br /><span style={{ color: T.teal }}>Mision a mision.</span>
        </h1>
        <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.7, marginBottom: 40, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
          T&D te guia para construir un negocio sano que genere empleos. Completa misiones, sube de nivel y cuando tu empresa madure, accede a fondeo colectivo.
        </p>
        <button onClick={onNext} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.teal, color: T.bg, fontWeight: 700, fontSize: 15, padding: '14px 32px', clipPath: CLIP, border: 'none', cursor: 'pointer', fontFamily: font, letterSpacing: '.3px' }}>
          Empezar partida →
        </button>
      </div>
    </div>
  );
}

/* ── Form Screen ──────────────────────────────── */
function FormScreen({ formName, setFormName, formGiro, setFormGiro, formGiroOtro, setFormGiroOtro, formIdentidad, setFormIdentidad, formStage, setFormStage, formCountryId, formRegionId, formCity, onLocationChange, saving, onSubmit }) {
  const inputStyle = { width: '100%', background: T.s3, border: `1px solid ${T.border}`, color: T.text, fontFamily: font, fontSize: 14, padding: '10px 14px', outline: 'none', clipPath: CLIP, boxSizing: 'border-box' };
  const canSubmit = formName.trim() && formIdentidad && !saving && !(formGiro === 'Otro' && !formGiroOtro.trim());
  return (
    <div style={{ background: T.bg, backgroundImage: `radial-gradient(ellipse 80% 60% at 50% 100%, rgba(93,193,185,.08) 0%, transparent 70%)`, minHeight: '100vh', fontFamily: font, color: T.text }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(93,193,185,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(93,193,185,.03) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: serif, fontSize: 13, letterSpacing: 4, color: T.teal, marginBottom: 36, textTransform: 'uppercase' }}>Thinker & Doer</div>
        <h2 style={{ fontFamily: serif, fontSize: 26, marginBottom: 8 }}>Cuentanos de ti y tu negocio</h2>
        <p style={{ fontSize: 14, color: T.muted, marginBottom: 28 }}>Esto define tu arbol de misiones y te conecta con la comunidad.</p>
        <div style={{ background: T.s2, border: `1px solid ${T.border}`, clipPath: CLIP, padding: 32, maxWidth: 480, margin: '0 auto 40px', textAlign: 'left' }}>

          {/* Identidad: Thinker / Doer */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.muted, letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 8 }}>Tu identidad *</label>
            <div style={{ display: 'flex', gap: 12 }}>
              {[{ id: 'thinker', label: 'Thinker', desc: 'Ideas sin ejecucion', color: T.mango }, { id: 'doer', label: 'Doer', desc: 'Ejecucion sin ideas', color: T.teal }].map(card => (
                <div key={card.id} onClick={() => setFormIdentidad(card.id)} style={{
                  flex: 1, padding: '16px 14px', cursor: 'pointer', textAlign: 'center',
                  background: formIdentidad === card.id ? `${card.color}12` : T.s3,
                  border: `2px solid ${formIdentidad === card.id ? card.color : T.border}`,
                  clipPath: CLIP, transition: '.2s',
                }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{card.id === 'thinker' ? '💡' : '⚡'}</div>
                  <div style={{ fontFamily: serif, fontSize: 16, marginBottom: 2, color: formIdentidad === card.id ? card.color : T.text }}>{card.label}</div>
                  <div style={{ fontSize: 11, color: T.muted }}>{card.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Nombre del negocio */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.muted, letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 6 }}>Nombre del negocio *</label>
            <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Ej. Talabarteria GA" style={inputStyle} />
          </div>

          {/* Giro */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.muted, letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 6 }}>Giro</label>
            <select value={formGiro} onChange={e => setFormGiro(e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
              <option value="">Selecciona tu giro</option>
              {GIROS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            {formGiro === 'Otro' && (
              <div style={{ marginTop: 12, animation: 'fadeIn 0.3s ease' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.8px', color: T.dim, textTransform: 'uppercase', marginBottom: 6 }}>
                  ¿CUÁL ES TU GIRO? *
                </label>
                <input
                  value={formGiroOtro} onChange={e => setFormGiroOtro(e.target.value)}
                  placeholder="Ej. Seguros y asesoría financiera"
                  style={{ width: '100%', background: T.s3, border: `1px solid ${T.border}`, color: T.text, fontFamily: font, fontSize: 14, padding: '10px 14px', outline: 'none', clipPath: CLIP, boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: 11, color: T.dim, marginTop: 4 }}>Lo usamos para conectarte con negocios parecidos.</div>
              </div>
            )}
          </div>

          {/* Etapa */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.muted, letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 6 }}>Etapa de tu negocio</label>
            <select value={formStage} onChange={e => setFormStage(e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
              <option value="">Selecciona etapa</option>
              {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Ubicacion */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.muted, letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 8 }}>Ubicacion</label>
            <LocationCascade
              value={{ countryId: formCountryId, regionId: formRegionId, cityName: formCity }}
              onChange={onLocationChange}
            />
          </div>

          <button onClick={onSubmit} disabled={!canSubmit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: canSubmit ? T.teal : T.s3, color: canSubmit ? T.bg : T.dim, fontWeight: 700, fontSize: 15, padding: '14px 32px', clipPath: CLIP, border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed', fontFamily: font, width: '100%', marginTop: 8, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Creando...' : 'Crear mi arbol →'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tutorial Screen ──────────────────────────── */
const TUTORIAL_CARDS = [
  { icon: '🌳', n: '01', h: 'Tu arbol', p: '10 mundos, cada uno con misiones clave. Completa mundos para subir de nivel.' },
  { icon: '🎯', n: '02', h: 'A tu ritmo', p: 'Llena cada mision cuando tengas evidencia real. Sin prisa, con proposito.' },
  { icon: '⚔️', n: '03', h: 'Vence al jefe', p: 'Cada mundo tiene un OKR (Objetivo + Resultado Clave). Cumplirlo desbloquea el siguiente.' },
  { icon: '🏆', n: '04', h: 'El premio', p: 'Al madurar, tu empresa accede a crowdfunding. La comunidad puede invertir en ti.' },
];

function TutorialScreen({ onStart }) {
  return (
    <div style={{ background: T.bg, backgroundImage: `radial-gradient(ellipse 80% 60% at 50% 100%, rgba(93,193,185,.08) 0%, transparent 70%)`, minHeight: '100vh', fontFamily: font, color: T.text }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(93,193,185,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(93,193,185,.03) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '80px 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: serif, fontSize: 13, letterSpacing: 4, color: T.teal, marginBottom: 48, textTransform: 'uppercase' }}>Thinker & Doer</div>
        <h2 style={{ fontFamily: serif, fontSize: 26, marginBottom: 6 }}>Tu arbol te espera</h2>
        <p style={{ fontSize: 14, color: T.muted, marginBottom: 28 }}>Rapido: asi funciona el juego.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, maxWidth: 600, margin: '0 auto 32px' }}>
          {TUTORIAL_CARDS.map(c => (
            <div key={c.n} style={{ background: T.s2, border: `1px solid ${T.border}`, clipPath: CLIP, padding: 20, textAlign: 'left' }}>
              <div style={{ fontSize: 24, marginBottom: 10 }}>{c.icon}</div>
              <div style={{ fontSize: 11, color: T.teal, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>{c.n}</div>
              <div style={{ fontFamily: serif, fontSize: 16, marginBottom: 6 }}>{c.h}</div>
              <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{c.p}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={onStart} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.teal, color: T.bg, fontWeight: 700, fontSize: 15, padding: '14px 32px', clipPath: CLIP, border: 'none', cursor: 'pointer', fontFamily: font }}>
            Comenzar! →
          </button>
          <button onClick={onStart} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', color: T.muted, fontWeight: 600, fontSize: 13, padding: '10px 20px', border: `1px solid ${T.border}`, cursor: 'pointer', clipPath: CLIP, fontFamily: font }}>
            Saltar tutorial
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Salud Administrativa ────────────────────── */
function calcularSaludAdmin(misiones, progreso) {
  const adminMissions = (misiones || []).filter(m => m.rama === 'Administración');
  if (adminMissions.length === 0) return 0;
  const maxScore = adminMissions.length * 3; // foto=3 is the max per mission
  let score = 0;
  adminMissions.forEach(m => {
    const p = (progreso || []).find(pr => pr.mision_id === m.id);
    if (p) {
      const nivel = p.evidencia_nivel || 'nota';
      score += nivel === 'foto' ? 3 : nivel === 'link' ? 2 : 1;
    }
  });
  return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
}

function SaludAdminIndicator({ misiones, progreso }) {
  const salud = calcularSaludAdmin(misiones, progreso);
  const color = salud >= 70 ? '#1D9E75' : salud >= 40 ? T.mango : T.coral;
  const label = salud >= 70 ? 'Saludable' : salud >= 40 ? 'En progreso' : 'Necesita atencion';
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>Salud Administrativa</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ flex: 1, height: 6, background: T.s3, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${salud}%`, background: color, borderRadius: 3, transition: '.4s' }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 36, textAlign: 'right' }}>{salud}%</span>
      </div>
      <div style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

/* ── Left Panel (Player Card, Stats, Bars, Stack, Eras) ── */
function LeftPanel({ negocio, estado, eras, misiones, progreso, pctComplete, mundos }) {
  // Stack slots: show unlocked from vencido worlds
  const stackSlots = mundos
    .filter(w => estado.mundoVencidoMap[w.id] && w.stack_upgrade)
    .map(w => w.stack_upgrade);

  return (
    <div style={{ background: T.s1, borderRight: `1px solid ${T.border}`, overflowY: 'auto', padding: 16 }}>
      {/* Business info */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>Negocio</div>
        <div style={{ fontFamily: serif, fontSize: 18, marginBottom: 2 }}>{negocio?.nombre || 'Mi Negocio'}</div>
        <div style={{ fontSize: 12, color: T.muted }}>{negocio?.giro || 'Giro no definido'}</div>
      </div>

      {/* Level badge + insignias */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.s3, border: `1px solid ${T.border}`, clipPath: CLIP, padding: '6px 12px', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
          <span style={{ fontSize: 20, color: T.mango, fontFamily: serif }}>{String(estado.nivel).padStart(2, '0')}</span> Nivel
        </div>
        {estado.insignias.slice(0, 2).map((ins, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 3, background: 'rgba(93,193,185,.12)', color: T.teal, border: '1px solid rgba(93,193,185,.3)' }}>
            🏅 {ins}
          </span>
        ))}
      </div>

      {/* Stats */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>Estadisticas</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <StatChip label="Pines" value={estado.pines} />
          <StatChip label="Insignias" value={estado.insignias.length} />
          <StatChip label="Completado" value={`${pctComplete}%`} />
        </div>
      </div>

      {/* Salud Administrativa */}
      <SaludAdminIndicator misiones={misiones} progreso={progreso} />

      {/* Ramas horizontal bars */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>Ramas del Negocio</div>
        {RAMAS.map((rama, i) => {
          const val = estado.stats[RAMAS_FULL[i]] || 0;
          const max = 5;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ width: 52, fontSize: 11, color: T.dim, textAlign: 'right', flexShrink: 0 }}>{rama}</div>
              <div style={{ flex: 1, height: 4, background: T.s3, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(val / max) * 100}%`, background: T.teal, borderRadius: 2 }} />
              </div>
              <div style={{ width: 16, fontSize: 11, fontWeight: 700, color: val > 0 ? T.teal : T.dim, textAlign: 'right' }}>{val}</div>
            </div>
          );
        })}
      </div>

      {/* Stack slots */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>Stack de herramientas</div>
        {stackSlots.length > 0 ? stackSlots.map((s, i) => (
          <div key={i} style={{ background: T.s2, border: `1px solid ${T.teal}`, clipPath: CLIP, padding: 10, marginBottom: 6, fontSize: 11, textAlign: 'center', color: T.text }}>
            <div style={{ fontSize: 10, color: T.teal, fontWeight: 700, marginBottom: 2 }}>DESBLOQUEADO</div>
            {s}
          </div>
        )) : null}
        {[...Array(Math.max(0, 3 - stackSlots.length))].map((_, i) => (
          <div key={`empty-${i}`} style={{ background: T.s2, border: `1px dashed ${T.border}`, clipPath: CLIP, padding: 10, marginBottom: 6, fontSize: 11, color: T.dim, textAlign: 'center' }}>
            Ranura vacia · Nivel {String((stackSlots.length + i) * 2).padStart(2, '0')}+
          </div>
        ))}
      </div>

      {/* Era progress */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>Eras</div>
        {eras.map(e => {
          const total = e.worlds.length;
          const pct = total > 0 ? (e.done / total) * 100 : 0;
          return (
            <div key={e.era} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, display: 'flex', justifyContent: 'space-between' }}>
                <span>Era {e.era + 1} · {e.name}</span>
                <span style={{ color: T.dim, fontSize: 10 }}>{e.done}/{total}</span>
              </div>
              <div style={{ fontSize: 10, color: T.dim, marginBottom: 4, letterSpacing: '.3px' }}>
                {ERA_INSIGNIAS[e.era]}
              </div>
              <div style={{ height: 4, background: T.s3, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: T.teal, borderRadius: 2, width: `${pct}%`, transition: '.3s' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.mango }}>{value}</div>
      <div style={{ fontSize: 10, color: T.dim }}>{label}</div>
    </div>
  );
}

/* ── Radar Chart (SVG hexagonal) ──────────────── */
function RadarChart({ ramas, maxVal }) {
  const cx = 80, cy = 80, R = 55;
  const n = ramas.length;
  const angle = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i, r) => {
    const a = angle(i);
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const gridLevels = [0.25, 0.5, 0.75, 1];

  const dataPoints = ramas.map((r, i) => {
    const frac = maxVal > 0 ? Math.min(r.val / maxVal, 1) : 0;
    return point(i, R * frac);
  });
  const dataPoly = dataPoints.map(p => p.join(',')).join(' ');

  return (
    <div style={{ position: 'relative', width: 160, height: 160, margin: '0 auto' }}>
      <svg viewBox="0 0 160 160" style={{ width: '100%', height: '100%' }}>
        {gridLevels.map(l => (
          <polygon
            key={l}
            points={ramas.map((_, i) => point(i, R * l).join(',')).join(' ')}
            fill="none" stroke={T.border} strokeWidth="1"
          />
        ))}
        {ramas.map((_, i) => (
          <line key={i} x1={cx} y1={cy} x2={point(i, R)[0]} y2={point(i, R)[1]} stroke={T.border} strokeWidth="0.5" />
        ))}
        <polygon points={dataPoly} fill="rgba(93,193,185,.2)" stroke={T.teal} strokeWidth="1.5" />
        {ramas.map((r, i) => {
          const [lx, ly] = point(i, R + 14);
          return (
            <text key={i} x={lx} y={ly} textAnchor="middle" fill={T.dim} fontSize="7" fontFamily={font}>
              {r.name.slice(0, 6)} {r.val}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Era bands for center tree ───────────────── */
const ERA_BANDS = [
  { era: 0, label: 'ERA 1 \u00b7 EXISTENCIA', yStart: 55, yEnd: 100, insignia: "It's Alive!" },
  { era: 1, label: 'ERA 2 \u00b7 SUPERVIVENCIA', yStart: 42, yEnd: 55, insignia: 'Still Standing' },
  { era: 2, label: 'ERA 3 \u00b7 \u00c9XITO', yStart: 20, yEnd: 42, insignia: 'Boss Mode' },
  { era: 3, label: 'ERA 4 \u00b7 DESPEGUE', yStart: 2, yEnd: 20, insignia: 'Liftoff' },
  { era: 4, label: 'ERA 5 \u00b7 MADUREZ', yStart: 0, yEnd: 2, insignia: 'Legacy' },
];

/* ── Tree SVG (Center Panel) — div-based nodes ── */
function TreeSVG({ mundos, estado, selectedWorld, setSelectedWorld, misiones, progreso }) {
  const [hoveredWorld, setHoveredWorld] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Build connection list
  const connections = [];
  for (let i = 0; i < NODE_POS.length - 1; i++) {
    if (i === 7) {
      connections.push({ fromIdx: 7, toIdx: 8 });
      connections.push({ fromIdx: 7, toIdx: 9 });
      break;
    }
    connections.push({ fromIdx: i, toIdx: i + 1 });
  }

  // Active node position for radial glow
  const activePos = NODE_POS[estado.mundoActivo];

  return (
    <div style={{
      position: 'relative', width: '100%', height: '100%',
      backgroundImage: 'linear-gradient(rgba(93,193,185,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(93,193,185,.04) 1px,transparent 1px)',
      backgroundSize: '28px 28px',
    }}>

      {/* Radial glow behind active node */}
      {activePos && !estado.mundoVencidoMap[estado.mundoActivo] && (
        <div style={{
          position: 'absolute',
          left: `${activePos.xPct}%`, top: `${activePos.y}%`,
          transform: 'translate(-50%, -50%)',
          width: 420, height: 420, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(93,193,185,.12) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />
      )}

      {/* Era band dashed lines + labels */}
      {ERA_BANDS.map((band, i) => (
        <React.Fragment key={i}>
          {band.yStart > 0 && (
            <div style={{
              position: 'absolute', left: 0, right: 0, top: `${band.yStart}%`, height: 1,
              background: 'repeating-linear-gradient(90deg, rgba(107,112,132,.25) 0px, rgba(107,112,132,.25) 6px, transparent 6px, transparent 14px)',
              pointerEvents: 'none', zIndex: 0,
            }} />
          )}
          <div style={{
            position: 'absolute', right: 12, top: `calc(${band.yStart}% + 6px)`,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 8, textTransform: 'uppercase',
            letterSpacing: '0.12em', color: 'rgba(107,112,132,.5)', pointerEvents: 'none', zIndex: 0,
          }}>
            {band.label}
          </div>
        </React.Fragment>
      ))}

      {/* SVG layer for connection lines only */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}
        viewBox="0 0 100 100" preserveAspectRatio="none">
        {connections.map((conn, ci) => {
          const from = NODE_POS[conn.fromIdx];
          const to = NODE_POS[conn.toIdx];
          const bothDone = estado.mundoVencidoMap[conn.fromIdx] && estado.mundoVencidoMap[conn.toIdx];
          const fromDone = estado.mundoVencidoMap[conn.fromIdx];
          return (
            <line key={ci}
              x1={from.xPct} y1={from.y} x2={to.xPct} y2={to.y}
              stroke={bothDone ? T.teal : fromDone ? T.teal : T.border}
              strokeWidth="0.3"
              strokeDasharray={bothDone ? 'none' : '0.8,0.8'}
              opacity={bothDone ? 1 : fromDone ? 0.5 : 0.6}
            />
          );
        })}
      </svg>

      {/* Node divs + labels */}
      {mundos.map((w) => {
        const pos = NODE_POS[w.id];
        if (!pos) return null;
        const done = estado.mundoVencidoMap[w.id];
        const active = w.id === estado.mundoActivo;
        const isLeftNode = pos.xPct <= 50;

        // Mission counts for label
        const worldMissions = misiones.filter(m => m.mundo_id === w.id && !m.side_quest);
        const worldDone = progreso.filter(p => worldMissions.some(m => m.id === p.mision_id)).length;

        return (
          <React.Fragment key={w.id}>
            {/* Node circle */}
            <div
              onClick={() => setSelectedWorld(w.id)}
              onMouseEnter={(e) => { setHoveredWorld(w.id); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
              onMouseMove={(e) => { setTooltipPos({ x: e.clientX, y: e.clientY }); }}
              onMouseLeave={() => setHoveredWorld(null)}
              style={{
                position: 'absolute',
                left: `${pos.xPct}%`,
                top: `${pos.y}%`,
                transform: 'translate(-50%, -50%)',
                width: 64, height: 64,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                background: done ? 'rgba(93,193,185,.15)' : active ? T.bg : T.s3,
                border: `${active ? 3 : 2}px solid ${done ? T.teal : active ? T.teal : w.id === 7 ? T.mango : T.border}`,
                cursor: 'pointer',
                transition: 'transform .2s, box-shadow .2s',
                animation: active ? 'arbol-pulse 1.8s ease-in-out infinite' : 'none',
                zIndex: 2,
              }}
            >
              {done ? (
                <span style={{ fontSize: 22, color: T.teal }}>&#10003;</span>
              ) : (
                <>
                  <span style={{ fontSize: 16, fontWeight: 800, color: done || active ? T.teal : T.dim }}>
                    {String(w.id).padStart(2, '0')}
                  </span>
                  {!done && !active && <span style={{ fontSize: 9, color: T.dim }}>&#128274;</span>}
                </>
              )}
            </div>

            {/* Label next to node */}
            <div style={{
              position: 'absolute',
              left: isLeftNode ? `calc(${pos.xPct}% + 44px)` : undefined,
              right: !isLeftNode ? `calc(${100 - pos.xPct}% + 44px)` : undefined,
              top: `${pos.y}%`,
              transform: 'translateY(-50%)',
              textAlign: isLeftNode ? 'left' : 'right',
              whiteSpace: 'nowrap',
              zIndex: 1,
              pointerEvents: 'none',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: T.dim, fontFamily: "'JetBrains Mono', monospace" }}>
                MUNDO {String(w.id).padStart(2, '0')} &middot; {worldDone}/{worldMissions.length}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: done ? T.teal : active ? T.text : T.muted }}>
                {w.nombre}
              </div>
              {w.id === 7 && (
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: T.mango, marginTop: 2 }}>
                  &#9734; CROWDFUNDING
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}

      {/* Hover tooltip */}
      {hoveredWorld !== null && (() => {
        const hw = mundos.find(m => m.id === hoveredWorld);
        if (!hw) return null;
        let tx = tooltipPos.x + 16;
        let ty = tooltipPos.y + 16;
        if (tx + 240 > window.innerWidth) tx = tooltipPos.x - 240;
        if (ty + 100 > window.innerHeight) ty = tooltipPos.y - 100;
        return (
          <div style={{
            position: 'fixed', left: tx, top: ty,
            background: T.s1, border: `1px solid ${T.border}`, clipPath: CLIP,
            padding: '12px 16px', width: 220, zIndex: 999, pointerEvents: 'none',
          }}>
            <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: T.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
              Mundo {String(hw.id).padStart(2, '0')} &middot; {hw.nombre}
            </div>
            <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.4, marginBottom: 8 }}>
              {hw.objetivo || ''}
            </div>
            <div style={{ fontSize: 11, color: T.mango }}>
              {hw.insignia || ''}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ── Right Panel (World Details + Missions) ───── */
function RightPanel({ selWorld, selMissions, selDone, progreso, misiones, onMissionClick, estado, selectedWorld }) {
  if (!selWorld) return <div style={{ background: T.s1, borderLeft: `1px solid ${T.border}`, padding: 16 }} />;

  const mainCount = selMissions.filter(m => !m.side_quest).length;
  const krs = selWorld.krs || [];

  return (
    <div style={{ background: T.s1, borderLeft: `1px solid ${T.border}`, overflowY: 'auto', padding: 16 }}>
      {/* World header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: T.teal }}>
            MUNDO {String(selWorld.id).padStart(2, '0')}
          </div>
          {selDone.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 3, background: 'rgba(93,193,185,.12)', color: T.teal, border: '1px solid rgba(93,193,185,.3)' }}>
              {selDone.length} / {mainCount} misiones
            </span>
          )}
        </div>
        <div style={{ fontFamily: serif, fontSize: 20, marginBottom: 6 }}>{selWorld.nombre}</div>
        <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{selWorld.descripcion || selWorld.pregunta || ''}</div>
      </div>

      {/* Mundo 07 Crowdfunding Banner */}
      {selectedWorld === 7 && (
        <div style={{ background: 'rgba(235,99,107,.1)', border: `1px solid ${T.coral}`, clipPath: CLIP, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, flexShrink: 0 }}>◆</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.coral }}>Aviso de Luca</div>
          </div>
          <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.7 }}>
            Antes de pedir dinero a la comunidad, Luca revisara tu expediente completo: contabilidad, activos, nomina y estructura legal. Si algo no cuadra, no se abre la ronda. El crowdfunding es un privilegio, no un derecho.
          </div>
        </div>
      )}

      {/* Insignia del mundo */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, padding:'10px 14px', background:`rgba(253,190,2,.08)`, border:`1px solid ${T.mango}`, clipPath:CLIP }}>
        <svg width="32" height="32" viewBox="0 0 28 28"><polygon points="14,1 26,7.5 26,20.5 14,27 2,20.5 2,7.5" fill="none" stroke={T.mango} strokeWidth="1.5"/><text x="14" y="16" textAnchor="middle" fontSize="10" fill={T.mango}>⬡</text></svg>
        <div>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:1, color:T.mango }}>INSIGNIA</div>
          <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{selWorld?.insignia}</div>
        </div>
        {estado.mundoVencidoMap[selectedWorld] && <span style={{ marginLeft:'auto', fontSize:11, fontWeight:700, color:T.teal }}>✓ DESBLOQUEADA</span>}
      </div>

      {/* OKR box */}
      <div style={{ background: T.s2, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.mango}`, clipPath: CLIP, padding: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: T.mango, marginBottom: 4 }}>OKR · OBJETIVO</div>
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>{selWorld.objetivo}</div>
        {krs.length > 0 && (
          <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
            {krs.map((kr, i) => (
              <li key={i} style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{typeof kr === 'string' ? kr : kr.texto || ''}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Mission list */}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: T.dim, textTransform: 'uppercase', margin: '16px 0 8px', paddingBottom: 6, borderBottom: `1px solid ${T.border}` }}>
        {selDone.length > 0 ? `Misiones · ${selDone.length} completadas` : `${selMissions.length} Misiones`}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {selMissions.map(m => {
          const done = progreso.some(p => p.mision_id === m.id);
          return (
            <div key={m.id}
              onClick={() => onMissionClick(m)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                background: T.s2, border: `1px solid ${done ? 'rgba(93,193,185,.3)' : T.border}`,
                clipPath: CLIP, cursor: done ? 'default' : 'pointer', opacity: done ? 0.7 : 1,
                transition: '.2s',
              }}
            >
              <div style={{
                width: 18, height: 18, border: `2px solid ${done ? T.teal : T.border}`,
                borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 11, marginTop: 1,
                background: done ? T.teal : 'transparent', color: done ? T.bg : 'transparent',
              }}>
                {done ? '✓' : ''}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{m.nombre}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{m.descripcion || ''}</div>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.8px', padding: '2px 6px', borderRadius: 2, background: 'rgba(93,193,185,.12)', color: T.teal, border: '1px solid rgba(93,193,185,.2)', display: 'inline-block', marginTop: 3 }}>
                  {m.rama}
                </span>
                {m.evidencia && (
                  <span style={{ fontSize:9, fontWeight:700, letterSpacing:'.5px', padding:'2px 6px', border:`1px solid ${T.mango}`, color:T.mango, display:'inline-block', marginTop:3, marginLeft:4 }}>{m.evidencia}</span>
                )}
                {m.side_quest && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 2, background: 'rgba(253,190,2,.12)', color: T.mango, border: '1px solid rgba(253,190,2,.3)', display: 'inline-block', marginTop: 3, marginLeft: 4 }}>
                    SIDE QUEST
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: T.mango, fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 4 }}>+1🌲</div>
            </div>
          );
        })}
      </div>

      {/* Stack upgrade suggestion */}
      {selWorld.stack_upgrade && (
        <div style={{ marginTop: 16, padding: 12, background: T.s3, border: `1px solid ${T.border}`, clipPath: CLIP }}>
          <div style={{ fontSize: 10, color: T.teal, fontWeight: 700, marginBottom: 4 }}>💡 STACK SUGERIDO</div>
          <div style={{ fontSize: 12, color: T.muted }}>
            Completa este mundo para desbloquear: <b style={{ color: T.text }}>{selWorld.stack_upgrade}</b>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Mission Modal (Hybrid Evidence) ─────────── */
function MissionModal({ mision, mode, setMode, note, setNote, submitting, onSubmit, onClose, mundos, misiones, selectedWorld, negocioId, evidenciaUrl, setEvidenciaUrl, linkValue, setLinkValue, useMobileLayout }) {
  const worldMissions = misiones.filter(m => m.mundo_id === mision.mundo_id);
  const misionIdx = worldMissions.findIndex(m => m.id === mision.id) + 1;
  const [uploading, setUploading] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');

  // Determine if this mission requires photo-only evidence
  const photoOnlyTypes = ['star', 'brand', 'social'];
  const isPhotoOnly = photoOnlyTypes.includes(mision.tipo_captura);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');

    // Show instant preview
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setUploading(true);

    try {
      const compressed = await compressImage(file);
      const ts = Date.now();
      const storagePath = `arbol/${negocioId || 'unknown'}/${mision.id}-${ts}.jpg`;
      const { error } = await supabase.storage.from('avatars').upload(storagePath, compressed, { upsert: true });
      if (error) {
        setUploadError(`Error: ${error.message}`);
        console.error('[Arbol] upload error', error);
        return;
      }
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(storagePath);
      setEvidenciaUrl(urlData.publicUrl);
      setUploadFileName(file.name);
    } catch (err) {
      setUploadError(`Error: ${err.message}`);
      console.error('[Arbol] upload error', err);
    } finally {
      setUploading(false);
    }
  };

  const PhotoUpload = () => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.8px', color: T.dim, textTransform: 'uppercase', marginBottom: 6 }}>
        {isPhotoOnly ? 'Foto obligatoria' : 'Foto (opcional)'}
      </label>
      {(evidenciaUrl || previewUrl) ? (
        <div style={{ background: 'rgba(93,193,185,.08)', border: `1px solid ${T.teal}`, borderRadius: 8, overflow: 'hidden' }}>
          <img src={evidenciaUrl || previewUrl} alt="Evidencia" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px' }}>
            <span style={{ fontSize: 12, color: T.teal, flex: 1 }}>{uploading ? '⏳ Subiendo...' : evidenciaUrl ? '✅ Foto lista' : '⏳ Procesando...'}</span>
            <button onClick={() => { setEvidenciaUrl(null); setUploadFileName(''); setPreviewUrl(null); }} style={{ background: 'none', border: 'none', color: T.coral, fontSize: 11, cursor: 'pointer', fontFamily: font }}>Quitar</button>
          </div>
        </div>
      ) : (
        <div style={{ border: `2px dashed ${T.border}`, background: T.s3, padding: 20, textAlign: 'center', cursor: 'pointer', position: 'relative' }}>
          <input type="file" accept="image/*" onChange={handleFileSelect} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
          <div style={{ fontSize: 24, marginBottom: 6 }}>{uploading ? '⏳' : '📷'}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{uploading ? 'Comprimiendo y subiendo...' : 'Toca para seleccionar foto'}</div>
        </div>
      )}
      {uploadError && (
        <div style={{ marginTop: 6, padding: '8px 12px', background: 'rgba(235,99,107,.1)', border: `1px solid ${T.coral}`, borderRadius: 6, fontSize: 12, color: T.coral }}>
          {uploadError}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: useMobileLayout ? T.bg : 'rgba(7,10,18,.92)',
      zIndex: 200, display: 'flex', flexDirection: 'column',
      alignItems: useMobileLayout ? 'stretch' : 'center',
      justifyContent: useMobileLayout ? 'flex-start' : 'center',
      padding: useMobileLayout ? 0 : 20,
    }}
      onClick={useMobileLayout ? undefined : onClose}>
      {/* Mobile close header */}
      {useMobileLayout && (
        <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', background: T.s1, borderBottom: `1px solid ${T.border}`, flexShrink: 0, gap: 12 }}>
          <button onClick={onClose} style={{ fontSize: 18, color: T.muted, cursor: 'pointer', lineHeight: 1, background: 'none', border: 'none', padding: '8px 4px', minWidth: 32, minHeight: 32 }}>&#10005;</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mision.nombre}</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.dim }}>MUNDO {String(mision.mundo_id).padStart(2,'0')}</div>
          </div>
        </div>
      )}
      <div style={{
        width: useMobileLayout ? '100%' : '100%',
        maxWidth: useMobileLayout ? 'none' : 520,
        maxHeight: useMobileLayout ? 'none' : '90vh',
        overflowY: 'auto', flex: useMobileLayout ? 1 : undefined,
        padding: useMobileLayout ? 16 : 0,
      }} onClick={useMobileLayout ? undefined : (e => e.stopPropagation())}>
        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {['hacer', 'yalo'].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              fontFamily: font, fontSize: 12, fontWeight: 600, padding: '6px 16px',
              border: `1px solid ${mode === m ? T.teal : T.border}`,
              background: mode === m ? T.teal : T.s2,
              color: mode === m ? T.bg : T.muted,
              cursor: 'pointer', clipPath: CLIP,
            }}>
              {m === 'hacer' ? 'Hacer mision' : 'Ya lo tengo'}
            </button>
          ))}
        </div>

        <div style={{ background: T.s2, border: `1px solid ${T.border}`, clipPath: CLIP, padding: 28 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, background: 'rgba(93,193,185,.1)', border: '1px solid rgba(93,193,185,.3)', clipPath: CLIP, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              {mode === 'hacer' ? '🎯' : '✅'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: T.teal, fontWeight: 700, letterSpacing: 1 }}>
                MUNDO {String(mision.mundo_id).padStart(2, '0')} · MISION {misionIdx} DE {worldMissions.length}
              </div>
              <div style={{ fontFamily: serif, fontSize: 22 }}>{mision.nombre}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 3, background: 'rgba(93,193,185,.12)', color: T.teal, border: '1px solid rgba(93,193,185,.3)' }}>
                +1 🌲
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 3, background: 'rgba(253,190,2,.12)', color: T.mango, border: '1px solid rgba(253,190,2,.3)' }}>
                {mision.evidencia || 'Evidencia'}
              </span>
            </div>
          </div>

          {/* Description */}
          <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7, marginBottom: 20 }}>
            {mision.descripcion || 'Completa esta mision y sube tu evidencia.'}
          </div>

          {/* Luca guide box */}
          <div style={{ background: T.s3, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.teal}`, clipPath: CLIP, padding: 14, marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700 }}>◆</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.teal }}>Consejo de Luca</div>
            </div>
            <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.8 }}>
              {isPhotoOnly
                ? 'Esta mision requiere evidencia fotografica. Sube una captura de pantalla o foto.'
                : 'Completa la mision paso a paso. Puedes subir foto, link o simplemente una nota.'}
            </div>
          </div>

          {/* Evidence section — conditional on tipo_captura */}
          {isPhotoOnly ? (
            /* Photo-only missions (star, brand, social) */
            <PhotoUpload />
          ) : (
            /* Standard missions: textarea + optional link + optional photo */
            <>
              <textarea
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Describe tu evidencia o cuentale a Luca que hiciste..."
                style={{ width: '100%', background: T.s3, border: `1px solid ${T.border}`, color: T.text, fontFamily: font, fontSize: 13, padding: 10, resize: 'vertical', minHeight: 70, marginBottom: 12, clipPath: CLIP, outline: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.8px', color: T.dim, textTransform: 'uppercase', marginBottom: 6 }}>Link (opcional)</label>
                <input
                  value={linkValue} onChange={e => setLinkValue(e.target.value)}
                  placeholder="https://..."
                  style={{ width: '100%', background: T.s3, border: `1px solid ${T.border}`, color: T.text, fontFamily: font, fontSize: 13, padding: '8px 12px', clipPath: CLIP, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <PhotoUpload />
            </>
          )}

          {/* Footer buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={onSubmit} disabled={submitting || (isPhotoOnly && !evidenciaUrl)} style={{
              fontFamily: font, fontSize: 13, fontWeight: 700, padding: '10px 20px',
              clipPath: CLIP, border: 'none', cursor: (submitting || (isPhotoOnly && !evidenciaUrl)) ? 'not-allowed' : 'pointer',
              background: T.teal, color: T.bg, opacity: (submitting || (isPhotoOnly && !evidenciaUrl)) ? 0.5 : 1,
            }}>
              {submitting ? 'Enviando...' : mode === 'hacer' ? 'Enviar evidencia →' : 'Ya lo tengo ✓'}
            </button>
            <button onClick={onClose} style={{
              fontFamily: font, fontSize: 13, fontWeight: 700, padding: '10px 20px',
              clipPath: CLIP, cursor: 'pointer',
              background: 'transparent', border: `1px solid ${T.border}`, color: T.muted,
            }}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Victory Modal ────────────────────────────── */
function VictoryModal({ world, onClose }) {
  if (!world) return null;
  const mundoNum = String(world.id).padStart(2, '0');
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(7,10,18,.92)' }} />
      <div style={{ position: 'relative', zIndex: 2, maxWidth: 440, width: '100%', background: T.s2, border: `1px solid ${T.mango}`, borderTop: `3px solid ${T.mango}`, clipPath: CLIP, padding: 36, textAlign: 'center' }}
        onClick={e => e.stopPropagation()}>

        {/* Hexagon badge */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <svg width="80" height="80" viewBox="0 0 80 80">
            <polygon points="40,4 74,22 74,58 40,76 6,58 6,22" fill="rgba(253,190,2,.12)" stroke={T.mango} strokeWidth="2" />
            <text x="40" y="38" textAnchor="middle" fontSize="22" fill={T.mango}>🏅</text>
            <text x="40" y="58" textAnchor="middle" fontSize="9" fill={T.mango} fontFamily={font} fontWeight="700">{world.insignia || ''}</text>
          </svg>
        </div>

        {/* Title */}
        <div style={{ fontFamily: serif, fontSize: 28, marginBottom: 8, color: T.text }}>
          ¡Mundo {mundoNum} vencido!
        </div>
        <div style={{ fontSize: 14, color: T.mango, fontWeight: 700, marginBottom: 20, letterSpacing: '.5px' }}>
          {world.insignia}
        </div>

        {/* Luca phrase placeholder */}
        <div style={{ background: T.s3, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.teal}`, clipPath: CLIP, padding: '14px 16px', marginBottom: 24, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, flexShrink: 0 }}>◆</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.teal }}>Luca dice:</div>
          </div>
          <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7, fontStyle: 'italic' }}>
            "{world.frase_luca || MUNDO_FRASES[world.id] || ''}"
          </div>
        </div>

        {/* Continuar button */}
        <button onClick={onClose} style={{
          fontFamily: font, fontSize: 14, fontWeight: 700,
          background: T.mango, color: T.bg,
          padding: '12px 36px', clipPath: CLIP, border: 'none', cursor: 'pointer',
        }}>
          Continuar →
        </button>
      </div>
    </div>
  );
}

/* ── Premium Block Modal ──────────────────────── */
function PremiumBlockModal({ onClose, navigate, user, profile }) {
  const perks = [
    'Acceso a Mundos 02-09 (Era 2 y Era 3)',
    'Validacion humana de evidencias por Luca',
    'Sesiones grupales mensuales',
    'Acceso a la red de mentores T&D',
    'Elegibilidad para crowdfunding al madurar',
    'Insignias avanzadas y stack premium',
  ];
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(7,10,18,.85)' }} />
      <div style={{ position: 'relative', zIndex: 2, maxWidth: 480, width: '100%', background: T.s2, border: `1px solid ${T.border}`, borderTop: `3px solid ${T.coral}`, clipPath: CLIP, padding: 32, textAlign: 'center' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <div style={{ fontFamily: serif, fontSize: 28, marginBottom: 8 }}>
          Mundo 02 requiere <span style={{ color: T.coral }}>Doer</span>
        </div>
        <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.7, marginBottom: 24 }}>
          Ya demostraste que puedes arrancar. Para seguir creciendo con Luca revisando tu evidencia y acceder a los mundos avanzados, activa Doer.
        </div>
        <div style={{ background: T.s3, border: `1px solid ${T.border}`, clipPath: CLIP, padding: 18, textAlign: 'left', marginBottom: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: T.teal, letterSpacing: 1, marginBottom: 12, margin: '0 0 12px' }}>CON DOER OBTIENES</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {perks.map((p, i) => (
              <li key={i} style={{ fontSize: 13, color: T.muted, lineHeight: 1.8, paddingLeft: 20, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: T.teal, fontWeight: 700 }}>✓</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ fontFamily: serif, fontSize: 32, marginBottom: 6 }}>
          $139 <span style={{ fontSize: 14, color: T.muted, fontFamily: font }}>MXN / mes</span>
        </div>
        <div style={{ fontSize: 11, color: T.dim, marginBottom: 4 }}>7 dias gratis · sin contrato, cancela cuando quieras</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
          <button onClick={() => startCheckout('doer', user, profile)} style={{ fontFamily: font, fontSize: 14, fontWeight: 700, background: T.teal, color: T.bg, padding: '12px 28px', clipPath: CLIP, border: 'none', cursor: 'pointer' }}>
            Probar 7 dias gratis →
          </button>
          <button onClick={onClose} style={{ fontFamily: font, fontSize: 13, fontWeight: 600, background: 'transparent', color: T.muted, padding: '10px 20px', border: `1px solid ${T.border}`, cursor: 'pointer', clipPath: CLIP }}>
            Seguir en Free
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tour Overlay ────────────────────────────── */
function TourOverlay({ step, totalSteps, data, zoneRef, onNext, onPrev, onSkip, onFinish, useMobileLayout }) {
  const [zoneRect, setZoneRect] = useState(null);

  useEffect(() => {
    if (zoneRef?.current) {
      const rect = zoneRef.current.getBoundingClientRect();
      setZoneRect(rect);
    }
  }, [step, zoneRef]);

  if (!zoneRect) return null;

  const isLast = step === totalSteps - 1;
  const isFirst = step === 0;

  const tooltipStyle = useMobileLayout ? {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    zIndex: 1002, padding: 20,
  } : {
    position: 'fixed',
    top: Math.max(20, zoneRect.top),
    left: data.zone === 'right' ? zoneRect.left - 400 : (data.zone === 'left' ? zoneRect.right + 16 : zoneRect.left + zoneRect.width / 2 - 190),
    zIndex: 1002, width: 380,
  };

  return (
    <>
      {/* Dark overlay with cutout */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, pointerEvents: 'none' }}>
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect x={zoneRect.left - 8} y={zoneRect.top - 8} width={zoneRect.width + 16} height={zoneRect.height + 16} rx="14" fill="black" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(3,6,10,0.72)" mask="url(#tour-mask)" />
        </svg>
      </div>

      {/* Highlight border around zone */}
      <div style={{
        position: 'fixed',
        left: zoneRect.left - 8, top: zoneRect.top - 8,
        width: zoneRect.width + 16, height: zoneRect.height + 16,
        border: '2px solid #5dc1b9', borderRadius: 14,
        zIndex: 1001, pointerEvents: 'none',
        animation: 'arbol-pulse 1.8s ease-in-out infinite',
        transition: 'all 0.45s ease',
      }} />

      {/* Tooltip */}
      <div style={{ ...tooltipStyle, pointerEvents: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{
          background: '#141c28', border: '1px solid #5dc1b9', borderRadius: 16,
          padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,.5)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#5dc1b9', fontWeight: 700 }}>
              PASO {step + 1} DE {totalSteps}
            </span>
            <button onClick={onSkip} style={{ background: 'none', border: 'none', color: '#8C97B0', fontSize: 12, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
              Saltar tour
            </button>
          </div>

          {/* Progress bars */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? '#5dc1b9' : '#1E2740' }} />
            ))}
          </div>

          {/* Title */}
          <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, margin: '0 0 8px', color: '#F3F4F6' }}>
            {data.title}
          </h3>

          {/* Text */}
          <p style={{ fontSize: 14, color: '#8C97B0', lineHeight: 1.7, margin: '0 0 14px' }}>
            {data.text}
          </p>

          {/* Tip box */}
          <div style={{
            padding: '10px 14px', background: 'rgba(253,190,2,.08)',
            border: '1px solid rgba(253,190,2,.25)', borderRadius: 8, marginBottom: 18,
          }}>
            <span style={{ fontSize: 12, color: '#FDBE02' }}>💡 {data.tip}</span>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {!isFirst && (
              <button onClick={onPrev} style={{
                background: 'transparent', border: '1px solid #1E2740', color: '#8C97B0',
                padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif", borderRadius: 8,
              }}>← Anterior</button>
            )}
            <button onClick={isLast ? onFinish : onNext} style={{
              background: '#5dc1b9', color: '#070A12', border: 'none',
              padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", borderRadius: 8,
            }}>
              {isLast ? '¡Empezar! →' : 'Siguiente →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Mobile Tree Canvas (zigzag fullscreen) ──── */
const MOBILE_NODE_POSITIONS = [
  { id: 0, xPct: 50, yPx: 770 },   // 00 Raiz (center bottom)
  { id: 1, xPct: 35, yPx: 698 },   // 01
  { id: 2, xPct: 65, yPx: 626 },   // 02
  { id: 3, xPct: 35, yPx: 554 },   // 03
  { id: 4, xPct: 65, yPx: 482 },   // 04
  { id: 5, xPct: 35, yPx: 410 },   // 05
  { id: 6, xPct: 65, yPx: 338 },   // 06
  { id: 7, xPct: 35, yPx: 266 },   // 07
  { id: 8, xPct: 65, yPx: 194 },   // 08
  { id: 9, xPct: 50, yPx: 130 },   // 09
];

const MOBILE_ERA_LINES = [
  { yPx: 740, label: 'Era 1 \u00b7 Existencia' },
  { yPx: 518, label: 'Era 2 \u00b7 Supervivencia' },
  { yPx: 374, label: 'Era 3 \u00b7 \u00c9xito' },
  { yPx: 230, label: 'Era 4 \u00b7 Despegue' },
  { yPx: 160, label: 'Era 5 \u00b7 Madurez' },
];

function MobileTreeCanvas({ mundos, misiones, progreso, estado, onNodeTap, bottomSheetWorld }) {
  const scrollRef = useRef(null);
  const activeNodeRef = useRef(null);

  // Auto-scroll to active node on mount
  useEffect(() => {
    if (activeNodeRef.current && scrollRef.current) {
      const timer = setTimeout(() => {
        activeNodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [estado.mundoActivo]);

  const treeHeight = 840;

  return (
    <div ref={scrollRef} style={{
      flex: 1, position: 'relative', overflowY: 'auto', overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      backgroundImage: 'linear-gradient(rgba(93,193,185,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(93,193,185,.04) 1px,transparent 1px)',
      backgroundSize: '28px 28px', background: T.bg,
      paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
    }}>
      <div style={{ position: 'relative', width: '100%', height: treeHeight,
        backgroundImage: 'linear-gradient(rgba(93,193,185,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(93,193,185,.04) 1px,transparent 1px)',
        backgroundSize: '28px 28px',
      }}>
        {/* SVG connection lines */}
        <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
          {MOBILE_NODE_POSITIONS.map((pos, i) => {
            if (i === MOBILE_NODE_POSITIONS.length - 1) return null;
            const next = MOBILE_NODE_POSITIONS[i + 1];
            const bothDone = estado.mundoVencidoMap[pos.id] && estado.mundoVencidoMap[next.id];
            const fromDone = estado.mundoVencidoMap[pos.id];
            return (
              <line key={i}
                x1={`${pos.xPct}%`} y1={pos.yPx}
                x2={`${next.xPct}%`} y2={next.yPx}
                stroke={bothDone ? T.teal : fromDone ? T.teal : T.border}
                strokeWidth={bothDone ? 2 : 1.5}
                strokeDasharray={bothDone ? 'none' : '4,3'}
                opacity={bothDone ? 1 : fromDone ? 0.5 : 0.6}
              />
            );
          })}
        </svg>

        {/* Era separator lines */}
        {MOBILE_ERA_LINES.map((era, i) => (
          <div key={i} style={{
            position: 'absolute', left: 0, right: 0, top: era.yPx,
            borderTop: '1px dashed rgba(107,112,132,.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 12,
          }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: T.dim,
              background: T.bg, padding: '0 4px', marginTop: -9, textTransform: 'uppercase',
            }}>{era.label}</span>
          </div>
        ))}

        {/* Radial glow behind active node */}
        {(() => {
          const activePos = MOBILE_NODE_POSITIONS.find(p => p.id === estado.mundoActivo);
          if (!activePos || estado.mundoVencidoMap[estado.mundoActivo]) return null;
          return (
            <div style={{
              position: 'absolute', left: `${activePos.xPct}%`, top: activePos.yPx,
              transform: 'translate(-50%, -50%)', width: 160, height: 160, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(93,193,185,.12) 0%, transparent 70%)',
              pointerEvents: 'none', zIndex: 0,
            }} />
          );
        })()}

        {/* Node circles + labels */}
        {mundos.map(w => {
          const pos = MOBILE_NODE_POSITIONS.find(p => p.id === w.id);
          if (!pos) return null;
          const done = estado.mundoVencidoMap[w.id];
          const active = w.id === estado.mundoActivo;
          const isLeftNode = pos.xPct <= 50;

          const worldMissions = misiones.filter(m => m.mundo_id === w.id && !m.side_quest);
          const worldDone = progreso.filter(p => worldMissions.some(m => m.id === p.mision_id)).length;

          return (
            <React.Fragment key={w.id}>
              {/* Node */}
              <div
                ref={active ? activeNodeRef : undefined}
                onClick={() => onNodeTap(w.id)}
                style={{
                  position: 'absolute', left: `${pos.xPct}%`, top: pos.yPx,
                  transform: 'translate(-50%, -50%)',
                  width: 56, height: 56, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                  background: done ? 'rgba(93,193,185,.15)' : active ? T.bg : T.s3,
                  border: `${active ? 3 : 2}px solid ${done ? T.teal : active ? T.teal : w.id === 7 ? T.mango : T.border}`,
                  cursor: 'pointer', zIndex: 2,
                  animation: active ? 'arbol-pulse 1.8s ease-in-out infinite' : 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {done ? (
                  <span style={{ fontSize: 20, color: T.teal, fontWeight: 700 }}>&#10003;</span>
                ) : (
                  <>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: active ? T.teal : w.id === 7 ? T.mango : T.dim }}>
                      {String(w.id).padStart(2, '0')}
                    </span>
                    {!active && <span style={{ fontSize: 10 }}>&#128274;</span>}
                  </>
                )}
                {/* CF badge for world 07 */}
                {w.id === 7 && (
                  <div style={{
                    position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)',
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: T.mango,
                    whiteSpace: 'nowrap', background: 'rgba(253,190,2,.1)',
                    border: '1px solid rgba(253,190,2,.4)', borderRadius: 10, padding: '1px 5px',
                  }}>&#9734; CF</div>
                )}
              </div>

              {/* Label */}
              <div style={{
                position: 'absolute',
                left: isLeftNode ? undefined : 12,
                right: isLeftNode ? 12 : undefined,
                top: pos.yPx,
                transform: 'translateY(-50%)',
                textAlign: isLeftNode ? 'right' : 'left',
                maxWidth: 140, zIndex: 1, pointerEvents: 'none',
                ...(isLeftNode
                  ? { right: `calc(${100 - pos.xPct}% + 38px)` }
                  : { left: `calc(${pos.xPct}% + 38px)` }
                ),
              }}>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textTransform: 'uppercase',
                  color: done ? T.teal : active ? T.teal : T.dim, lineHeight: 1.2,
                }}>
                  MUNDO {String(w.id).padStart(2, '0')} &middot; {worldDone}/{worldMissions.length}
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word',
                  color: done ? T.teal : active ? T.text : T.muted,
                }}>
                  {w.nombre}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
